import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import './App.css';
import { Button, Modal, Form, Dropdown } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactHowler from 'react-howler';

// ─── Broker backend switching ─────────────────────────────────
// Two backends supported: Kite (default) and Kotak. User toggles via
// header dropdown; choice persisted in localStorage. Page reloads on
// change so socket/state are cleanly reinitialised against new backend.
const KITE_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://127.0.0.1:5000";
const KOTAK_BACKEND_URL = process.env.REACT_APP_KOTAK_BACKEND_URL || "http://127.0.0.1:5002";
const BROKER_STORAGE_KEY = "tmx_broker";

function getActiveBroker() {
    // Reads localStorage. The pre-boot script in index.html intercepts
    // localStorage.getItem for broker keys and routes to sessionStorage,
    // making the choice per-tab even though the React code uses localStorage.
    try {
        const saved = (localStorage.getItem(BROKER_STORAGE_KEY) || "").toLowerCase();
        if (saved === "kite" || saved === "kotak") return saved;
    } catch (e) { /* ignore */ }
    return "kite"; // default
}

function getBackendUrl(broker) {
    const b = broker || getActiveBroker();
    return b === "kotak" ? KOTAK_BACKEND_URL : KITE_BACKEND_URL;
}

function setActiveBroker(broker) {
    const b = (broker || "").toLowerCase();
    if (b !== "kite" && b !== "kotak") return;
    if (getActiveBroker() === b) return;
    // localStorage.setItem is intercepted by the pre-boot script and routed
    // to sessionStorage, so this only affects the current tab.
    try { localStorage.setItem(BROKER_STORAGE_KEY, b); } catch (e) { /* ignore */ }
    window.location.reload();
}

function getBrokerDisplayName(broker) {
    const b = broker || getActiveBroker();
    return b === "kotak" ? "Kotak" : "Kite";
}

// 2026-05-11 (#3): Order-success sound — KOTAK ONLY (option-a per user spec).
//
// Plays a short ding via Web Audio API whenever the user places an order and
// the server responds with success — but only when the active broker is kotak
// (when the user is on kite, no sound, preserving current behaviour).
//
// Why Web Audio (vs an <audio> element + .mp3 file):
//   - Zero asset dependency. No file to ship, host on nginx, or worry about
//     path/cache. The synth is ~30 lines of code.
//   - Plays immediately even if user hasn't interacted with the page yet
//     (well, mostly — modern browsers still gate AudioContext until first
//     user gesture; we lazily-construct on first call so a click on the
//     order button satisfies that requirement before the success arrives).
//
// Sound profile: two short beeps (1000 Hz then 1400 Hz, 120ms each, 60ms gap).
// Total duration ~300ms — clearly audible but doesn't compete with toast.

let _orderSoundCtx = null;
function _getOrderSoundCtx() {
    if (_orderSoundCtx) return _orderSoundCtx;
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return null;
        _orderSoundCtx = new Ctx();
        return _orderSoundCtx;
    } catch (e) {
        return null;
    }
}

function playOrderSuccessSound() {
    if (getActiveBroker() !== "kotak") return;  // option (a) — kotak only
    const ctx = _getOrderSoundCtx();
    if (!ctx) return;
    // Some browsers suspend the context until the first user gesture; resume
    // it lazily. The placeorder button click that preceded this call IS the
    // gesture, so resume() should succeed silently.
    if (ctx.state === "suspended") {
        try { ctx.resume(); } catch (e) { /* ignore */ }
    }
    const playBeep = (freq, startOffset, durationSec) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        // Smooth attack + decay to avoid the audible click of a hard envelope
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + startOffset);
        gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + startOffset + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + startOffset + durationSec);
        osc.connect(gain).connect(ctx.destination);
        osc.start(ctx.currentTime + startOffset);
        osc.stop(ctx.currentTime + startOffset + durationSec + 0.02);
    };
    playBeep(1000, 0,    0.12);   // first beep
    playBeep(1400, 0.18, 0.12);   // second beep, 60ms after first finishes
}

// 2026-05-11 (#5): Draggable flying broker logo widget.
//
// Inspired by the OLD kite chatbot widget — a fixed-position, draggable pill
// that shows ONLY the active broker's name (kite OR kotak, never both).
//
// Sizing per user spec: HEIGHT = 2× the strike-distance button height
// (renderStrikeIntervalButtons uses minHeight: 30px, so logo is 60px tall).
// Width is auto-fit to text content with comfortable horizontal padding.
//
// Colours per user spec:
//   kite  → red background (#dc2626), white text
//   kotak → white background, red text (#dc2626)
//
// Position is persisted to localStorage (per-tab via the same Storage-shim
// the broker selector uses) under TMX_FLYING_LOGO_POS_KEY. On first mount we
// drop it in the top-right area; user can drag it anywhere on the viewport.
//
// Why a separate inline component (not a top-level file): avoids adding new
// files to the dev folder (matches the "surgical, minimal sprawl" rule);
// the widget is tightly coupled to getActiveBroker() / getBrokerDisplayName()
// which already live here.

const TMX_FLYING_LOGO_POS_KEY = "tmx_flying_logo_pos";

function BrokerFlyingLogo() {
    // We use the broker via state so it can react to runtime changes (today
    // setActiveBroker triggers a reload — but if that ever changes, we'd want
    // this to live-update). Mount-time read is fine here.
    const broker = getActiveBroker();
    const name = getBrokerDisplayName(broker);

    // Restore last position from localStorage; default = top-right.
    const initialPos = React.useMemo(() => {
        try {
            const raw = localStorage.getItem(TMX_FLYING_LOGO_POS_KEY);
            if (raw) {
                const p = JSON.parse(raw);
                if (typeof p.x === "number" && typeof p.y === "number") return p;
            }
        } catch (e) { /* ignore */ }
        // First-load default: tucked into top-right, below the header.
        const x = Math.max(window.innerWidth - 140, 16);
        const y = 110;
        return { x, y };
    }, []);

    const [pos, setPos] = React.useState(initialPos);
    const [dragging, setDragging] = React.useState(false);
    const dragOffsetRef = React.useRef({ dx: 0, dy: 0 });

    // Mouse/touch handlers — window-level listeners while dragging so the
    // pointer can leave the element bounds without losing the drag.
    React.useEffect(() => {
        if (!dragging) return;
        const onMove = (e) => {
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;
            // Constrain inside viewport so the logo can't get lost off-screen.
            const newX = Math.max(0, Math.min(window.innerWidth - 60, clientX - dragOffsetRef.current.dx));
            const newY = Math.max(0, Math.min(window.innerHeight - 60, clientY - dragOffsetRef.current.dy));
            setPos({ x: newX, y: newY });
        };
        const onUp = () => {
            setDragging(false);
            // Persist on drop, not on every pixel-move.
            setPos((p) => {
                try { localStorage.setItem(TMX_FLYING_LOGO_POS_KEY, JSON.stringify(p)); } catch (e) { /* ignore */ }
                return p;
            });
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        window.addEventListener("touchmove", onMove, { passive: false });
        window.addEventListener("touchend", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            window.removeEventListener("touchmove", onMove);
            window.removeEventListener("touchend", onUp);
        };
    }, [dragging]);

    const startDrag = (e) => {
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        dragOffsetRef.current = { dx: clientX - pos.x, dy: clientY - pos.y };
        setDragging(true);
        // Prevent text selection / page scroll on touch
        if (e.preventDefault) e.preventDefault();
    };

    const isKotak = broker === "kotak";
    const style = {
        position: "fixed",
        left: `${pos.x}px`,
        top: `${pos.y}px`,
        height: "60px",                       // 2× distance button (30px)
        padding: "0 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: isKotak ? "#ffffff" : "#dc2626",   // kotak white, kite red
        color:      isKotak ? "#dc2626" : "#ffffff",   // kotak red text, kite white
        border: isKotak ? "2px solid #dc2626" : "2px solid #ffffff",
        borderRadius: "12px",
        boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
        fontSize: "22px",
        fontWeight: 800,
        letterSpacing: "0.5px",
        userSelect: "none",
        cursor: dragging ? "grabbing" : "grab",
        zIndex: 9000,                         // below isFreezed overlay (9999)
        // Subtle pulse so users notice it on first load
        transition: dragging ? "none" : "transform 0.12s ease",
        transform: dragging ? "scale(1.04)" : "scale(1)",
    };

    return (
        <div
            style={style}
            onMouseDown={startDrag}
            onTouchStart={startDrag}
            title={`Active broker: ${name} (drag to move)`}
        >
            {name}
        </div>
    );
}

const baseUrl1 = getBackendUrl();

// Utility for file logging

/**
 * Generates a high-precision IST timestamp string.
 * @returns {string} Formatted timestamp e.g., "2024-06-25 12:34:56.789 IST"
 */
function getIndiaTimestamp() {
    const now = new Date();

    // Get the current UTC time in milliseconds
    const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);

    // IST is UTC + 5:30
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istTime = new Date(utcTime + istOffset);

    // Format the date and time parts
    const year = istTime.getUTCFullYear();
    const month = String(istTime.getUTCMonth() + 1).padStart(2, '0');
    const day = String(istTime.getUTCDate()).padStart(2, '0');
    const hours = String(istTime.getUTCHours()).padStart(2, '0');
    const minutes = String(istTime.getUTCMinutes()).padStart(2, '0');
    const seconds = String(istTime.getUTCSeconds()).padStart(2, '0');
    const milliseconds = String(istTime.getUTCMilliseconds()).padStart(3, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds} IST`;
}


const LogManager = {
    logs: [],

    log: function (component, action, details) {
        const isError = action.includes('Error') || action.includes('Failed');
        if (component === 'Socket' && (action === 'Connected' || action === 'Disconnected') && !isError) {
            return;
        }
        const timestamp = getIndiaTimestamp();
        const logEntry = {
            timestamp,
            component,
            action,
            details: typeof details === 'object' ? JSON.stringify(details, null, 2) : details
        };
        this.logs.push(logEntry);
        const formattedLog = `[${timestamp}] [${component}] [${action}]: ${logEntry.details}`;
        console.log(formattedLog);
        this.saveLogsDebounced();
        return formattedLog;
    },

    saveTimeout: null,
    saveLogsDebounced: function () {
        clearTimeout(this.saveTimeout);
        this.saveTimeout = setTimeout(() => this.saveLogs(), 5000);
    },

    saveLogs: function () {
        if (this.logs.length === 0) return;

        const today = new Date().toISOString().split('T')[0];
        const filename = `trade-logs-${today}.json`;

        const logsToSave = [...this.logs];
        this.logs = [];

        const payloadContent = {
            entries: logsToSave
        };

        const contentJsonString = JSON.stringify(payloadContent, null, 2);

        axios.post(`${baseUrl1}/save_logs`, {
            filename,
            content: contentJsonString // Send the stringified object
        })
            .then(response => {
                console.log(`Logs saved successfully: ${response.data.message || 'OK'}`);
            })
            .catch(error => {
                console.error('Failed to save logs:', error.response ? error.response.data : error.message);
                this.logs.unshift(...logsToSave);
            });
    }
};


// Throttle for large LTP data
const throttle = (func, wait = 1000) => {
    let lastCall = 0;
    let timeout;
    return (...args) => {
        const now = Date.now();
        const remaining = wait - (now - lastCall);
        if (remaining <= 0) {
            lastCall = now;
            func(...args);
        } else {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                lastCall = Date.now();
                func(...args);
            }, remaining);
        }
    };
};

const App = () => {
    const baseUrl = getBackendUrl();
    const activeBroker = getActiveBroker();
    const socket = useRef(null);

    // --------------- State ---------------
    const [data, setData] = useState({});
    const [ceSideData, setCeSideData] = useState([]);
    const [peSideData, setPeSideData] = useState([]);
    const [lockedCeSideData, setLockedCeSideData] = useState([]);
    const [lockedPeSideData, setLockedPeSideData] = useState([]);

    const [instrument, setInstrument] = useState('NIFTY');
    // SEBI price-feed-stall alert (2026-08-06): set from the 'index_price_resumed'
    // socket event — {index, price} of whichever index THIS client currently has
    // selected. null when nothing to show. Dismiss (click) just clears this local
    // state; the backend's own monitoring is untouched by that.
    const [stallAlert, setStallAlert] = useState(null);
    const [expiryDate, setExpiryDate] = useState(null);
    const [lotSizes, setLotSizes] = useState([]);
    const [expiryDates, setExpiryDates] = useState({});
    const [rawLtpData, setRawLtpData] = useState({});
    const [ltpData, setLtpData] = useState({});

    const [cePeValuesOptions, setCePeValuesOptions] = useState([]);
    const [isFreezed, setIsFreezed] = useState(false);
    const freezeTimerRef = useRef(null);

    const [pendingLtpTokens, setPendingLtpTokens] = useState(new Set());
    const [isUpdatingS3, setIsUpdatingS3] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [showSettings, setShowSettings] = useState(false);
    const [modalInstrument, setModalInstrument] = useState('NIFTY');
    const [modalExpiryDate, setModalExpiryDate] = useState(null);
    const [modalLotSizes, setModalLotSizes] = useState([]);
    const [modalCustomLotSizes, setModalCustomLotSizes] = useState(['', '', '']);
    const [numRows, setNumRows] = useState(10);
    const [itms, setNumItms] = useState(3);

    // Distances
    const [prevPeRequiredDistance, setPrevPeRequiredDistance] = useState(null);
    const [peBlink, setPeBlink] = useState(false);
    const [peDistanceChange, setPeDistanceChange] = useState(null);
    const [peRequiredDistanceTimestamp, setPeRequiredDistanceTimestamp] = useState(null);

    const [prevCeRequiredDistance, setPrevCeRequiredDistance] = useState(null);
    const [ceBlink, setCeBlink] = useState(false);
    const [ceDistanceChange, setCeDistanceChange] = useState(null);
    const [ceRequiredDistanceTimestamp, setCeRequiredDistanceTimestamp] = useState(null);

    const [currentTime, setCurrentTime] = useState(new Date());
    const [ceExitMethod, setCeExitMethod] = useState('S3');
    const [peExitMethod, setPeExitMethod] = useState('S3');

    // Alerts
    const [peEntryAlert, setPeEntryAlert] = useState(false);
    const [ceEntryAlert, setCeEntryAlert] = useState(false);
    const [peAlertDistance, setPeAlertDistance] = useState(5);
    const [ceAlertDistance, setCeAlertDistance] = useState(5);
    const [shouldPlaySound, setShouldPlaySound] = useState(false);

    const [modalNumRows, setModalNumRows] = useState(numRows);
    const [modalNumItms, setModalNumItms] = useState(itms);
    const [lastLtpTimestamp, setLastLtpTimestamp] = useState(Date.now());

    // Row actions
    const [rowActions, setRowActions] = useState({});
    const [peSelectedRows, setPeSelectedRows] = useState([]);
    const [ceSelectedRows, setCeSelectedRows] = useState([]);
    const [peHas3rdCheckboxError, setPeHas3rdCheckboxError] = useState(false);
    const [ceHas3rdCheckboxError, setCeHas3rdCheckboxError] = useState(false);

    // Strike intervals & lock
    const [ceSelectedStrikeInterval, setCeSelectedStrikeInterval] = useState(null);
    const [peSelectedStrikeInterval, setPeSelectedStrikeInterval] = useState(null);
    const [checkMargin, setCheckMargin] = useState(true);
    const [isStrikeLockActive, setIsStrikeLockActive] = useState(false);
    // Rate-limit toggle (added 2026-04-27): server-authoritative state synced
    // across all open UI tabs via Socket.IO 'rate_limit_state_update'.
    // ON (default) = client-side throttle of 10 orders/1.2s active.
    // OFF = throttle bypassed; Kite may return 'Too many requests' 429.
    const [rateLimitEnabled, setRateLimitEnabled] = useState(false);  // 2026-06-05: default OFF per user spec
    const [grpCheckMargin, setGrpCheckMargin] = useState(true);
    // **T3/T4 toggles (formerly S4)**
    // false means T3 is selected; true means T4 is selected.
    const [peS4, setPeS4] = useState(false);
    const [ceS4, setCeS4] = useState(false);

    // **S3 Multipliers** (default 1 means no multiplier)
    const [peS3Multiplier, setPeS3Multiplier] = useState(1);
    const [ceS3Multiplier, setCeS3Multiplier] = useState(1);

    // Market Protection % (1-100, no decimal) — sent with every order API call
    const [marketProtection, setMarketProtection] = useState(
        parseInt(localStorage.getItem("marketProtection") || "30", 10)
    );
    // Iceberg order toggle (placeholder — endpoint to be added later)
    const [icebergEnabled, setIcebergEnabled] = useState(false);

    // Orderbook mode toggle — enables circuit limit retry with MP=-1
    const [orderbookEnabled, setOrderbookEnabled] = useState(
        localStorage.getItem("orderbookEnabled") === "true"
    );
    // Failed order notifications (for floating red dot)
    const [failedOrderNotifications, setFailedOrderNotifications] = useState([]);
    // Draggable dot position
    const [dotPosition, setDotPosition] = useState(() => {
        const saved = localStorage.getItem("failDotPosition");
        return saved ? JSON.parse(saved) : { x: window.innerWidth - 60, y: window.innerHeight - 60 };
    });
    const [showFailPopup, setShowFailPopup] = useState(false);

    // Retry limit (0-5, 0=no retry)
    const [retryLimit, setRetryLimit] = useState(
        parseInt(localStorage.getItem("retryLimit") || "2", 10)
    );

    // Dark/Light mode
    const [isDarkMode, setIsDarkMode] = useState(true);

    useEffect(() => {
        document.body.classList.toggle('dark-mode', isDarkMode);
        document.body.classList.toggle('light-mode', !isDarkMode);
    }, [isDarkMode]);

    // Sync orderbook mode + retry limit with server on mount
    useEffect(() => {
        if (orderbookEnabled) {
            axios.post(`${baseUrl}/set_orderbook_mode`, { enabled: true }).catch(() => { });
        }
        axios.post(`${baseUrl}/set_retry_limit`, { limit: retryLimit }).catch(() => { });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Listen for order failure notifications from Socket.IO
    useEffect(() => {
        if (!socket.current) return;
        const handler = (data) => {
            if (!orderbookEnabled) return;
            const failures = data.failures || [];
            const newNotifs = failures.map((f, idx) => ({
                id: `${data.execution_id}-${idx}-${Date.now()}`,
                tradingsymbol: f.tradingsymbol,
                direction: f.direction,
                quantity: f.quantity,
                group: f.group,  // exchange type (NFO/BFO)
                error: f.error,
                timestamp: data.timestamp,
                execution_id: data.execution_id,
            }));
            setFailedOrderNotifications(prev => [...prev, ...newNotifs]);
        };
        socket.current.on('order_failure_notification', handler);
        return () => { socket.current?.off('order_failure_notification', handler); };
    }, [orderbookEnabled]);

    // requestId
    const requestIdRef = useRef(0);

    // Refs
    const oldCeS3DataRef = useRef(null);
    const oldPeS3DataRef = useRef(null);

    // Mappings
    const instrumentIdMapping = {
        NIFTY: 256265,
        FINNIFTY: 257801,
        BANKNIFTY: 260105,
        MIDCPNIFTY: 288009,
        SENSEX: 265,
        BANKEX: 274441,
        CRUDEOIL: 125002247,   // 2026-05-14: MCX commodity — token of front-month future at deploy time
    };

    const instrumentStrikeIntervals = {
        NIFTY: 50,
        BANKNIFTY: 100,
        FINNIFTY: 50,
        MIDCPNIFTY: 25,
        SENSEX: 100,
        BANKEX: 100,
        CRUDEOIL: 50,   // 2026-05-14: MCX commodity, strike step 50
    };

    // const lotSizeOptions = {
    //     NIFTY: [1800, 3600, 5400, 7200, 9000, 10800, 12600, 14400, 16200, 18000],
    //     FINNIFTY: [1800, 3600, 5400, 7200, 9000, 10800, 12600, 14400, 16200, 18000],
    //     // BANKNIFTY: [600, 1200, 1800, 2400, 3000, 3600, 4200, 4800, 5400, 6000],
    //     // MIDCPNIFTY: [2760, 5520, 8280, 11040, 13800, 16560, 19320, 22080, 24840, 27600],
    //     // BANKNIFTY: [875, 1750, 2625, 3500, 4375, 5250, 6125, 7000, 7875, 8750],
    //     BANKNIFTY: [595, 1190, 1785, 2380, 2975, 3570, 4165, 4760, 5355, 5950],
    //     MIDCPNIFTY: [2800, 5600, 8400, 11200, 14000, 16800, 19600, 22400, 25200, 28000],
    //     SENSEX: [1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000, 10000],
    //     BANKEX: [900, 1800, 2700, 3600, 4500, 5400, 6300, 7200, 8100, 9000],
    // };

    // // Maximum order quantities per instrument
    // const maxOrderQuantities = {
    //     NIFTY: 1800,
    //     FINNIFTY: 1800,
    //     BANKNIFTY: 595,
    //     MIDCPNIFTY: 2800,
    //     SENSEX: 1000,
    //     BANKEX: 900
    // };

    const lotSizeOptions = {
        // NIFTY: [1800, 3600, 5400, 7200, 9000, 10800, 12600, 14400, 16200, 18000],
        NIFTY: [1755, 3510, 5265, 7020, 8775, 10530, 12285, 14040, 15795, 17550],
        FINNIFTY: [1800, 3600, 5400, 7200, 9000, 10800, 12600, 14400, 16200, 18000],
        // MIDCPNIFTY: [2760, 5520, 8280, 11040, 13800, 16560, 19320, 22080, 24840, 27600],
        // BANKNIFTY: [875, 1750, 2625, 3500, 4375, 5250, 6125, 7000, 7875, 8750],
        // BANKNIFTY 900-era (2026-04-27 until 2026-08-03): [900, 1800, 2700, 3600, 4500, 5400, 6300, 7200, 8100, 9000]
        BANKNIFTY: [600, 1200, 1800, 2400, 3000, 3600, 4200, 4800, 5400, 6000],
        MIDCPNIFTY: [2800, 5600, 8400, 11200, 14000, 16800, 19600, 22400, 25200, 28000],
        SENSEX: [1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000, 10000],
        BANKEX: [900, 1800, 2700, 3600, 4500, 5400, 6300, 7200, 8100, 9000],
        // 2026-05-14: MCX commodity — 1 lot = 1 contract; max single-order qty is 100
        CRUDEOIL: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
    };

    // Maximum order quantities per instrument
    // BANKNIFTY reverted 2026-08-03: 900 -> 600 (lot_size remains 30)
    const maxOrderQuantities = {
        NIFTY: 1755,
        FINNIFTY: 1800,
        BANKNIFTY: 600,
        MIDCPNIFTY: 2800,
        SENSEX: 1000,
        BANKEX: 900,
        CRUDEOIL: 100,   // 2026-05-14: MCX commodity, max 100 per single order
    };

    // Move functionality state variables
    const [movingRows, setMovingRows] = useState({}); // Track which rows have M button active
    const [moveIndexValues, setMoveIndexValues] = useState({}); // Store the first text box values
    const [movePnlValues, setMovePnlValues] = useState({}); // Store the second text box values
    const [activeMoving, setActiveMoving] = useState({}); // Track which rows have active moving (blinking)
    const [selectedLotSizesForMove, setSelectedLotSizesForMove] = useState({}); // Track selected lot size for move
    const [executedQuantities, setExecutedQuantities] = useState({}); // Track executed quantities
    const [capturedDistances, setCapturedDistances] = useState({}); // Store distance values when M is activated

    // New state variables for enhanced move functionality
    const [pairedMoves, setPairedMoves] = useState({}); // Track paired M rows
    const [combinedPnlValues, setCombinedPnlValues] = useState({}); // Track combined PnL values

    // New state variables for enhanced move state tracking
    const [moveOperationStatus, setMoveOperationStatus] = useState({}); // Track operation status
    const [distanceChangedDuringMove, setDistanceChangedDuringMove] = useState({}); // Track distance changes

    // NEW STATE for preserving PnL values
    const [preservedPnlValues, setPreservedPnlValues] = useState({}); // Store initial PnL values when move is activated
    const [preservedMarginCheckResults, setPreservedMarginCheckResults] = useState({}); // Store margin check results

    // NEW STATE for 10ms continuous match tracking
    const continuousMatchIntervals = useRef({}); // Store interval references

    // Logging controls
    const [showLogs, setShowLogs] = useState(false);
    const [logEntries, setLogEntries] = useState([]);
    const [logFilter, setLogFilter] = useState('');

    // -------------------- USE EFFECTS --------------------
    // 1) On mount => fetch expiryDates
    useEffect(() => {
        fetchExpiryDates();
        // Initial log entry - session started
        LogManager.log('App', 'SessionStart', `Session started with instrument: ${instrument}`);
    }, []);

    // 2) Default expiry + lot sizes
    useEffect(() => {
        if (modalInstrument && expiryDates[modalInstrument] && expiryDates[modalInstrument].length > 0) {
            setModalExpiryDate(expiryDates[modalInstrument][0]);
            // If no lot sizes set, fallback to default options
            if (lotSizes.length === 0 && lotSizeOptions[modalInstrument]) {
                setModalLotSizes(lotSizeOptions[modalInstrument].slice(0, 3));
            }
        }
    }, [modalInstrument, expiryDates, lotSizes]);

    // 3) Socket setup
    useEffect(() => {
        // socket.io setup  
        socket.current = io('https://tmxapiv3.trademaxx.online', {
            // v3: cross-domain socket to the V3 API (default /socket.io/ path)
            transports: ['websocket'],
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            timeout: 20000
        });

        socket.current.on('connect', () => {
            LogManager.log('Socket', 'Connected', 'Connected to server');
        });

        // data_response
        socket.current.on('data_response', (responseData) => {
            if (responseData.requestId !== requestIdRef.current) {
                LogManager.log('Socket', 'StaleResponse', `Ignoring stale response => got ${responseData.requestId}, need ${requestIdRef.current}`);
                return;
            }

            if (responseData.error) {
                toast.error(responseData.error);
                LogManager.log('Socket', 'Error', responseData.error);
                unfreezeUI();
            } else {
                setData((prev) => ({
                    ...prev,
                    ATM_VALUE: responseData.ATM_VALUE,
                    Exact_Synthetic_Future: responseData.Exact_Synthetic_Future,
                    PE_SIDE: responseData.PE_SIDE,
                    CE_SIDE: responseData.CE_SIDE,
                }));

                let newTokens = responseData.tokens || [];

                // Lock vs Unlock
                if (isStrikeLockActive) {
                    if (isUpdatingS3) {
                        setLockedCeSideData((prev) =>
                            prev.map((item, idx) => {
                                const newRow = responseData.CE_Results[idx];
                                return {
                                    ...item,
                                    'S3-Strike': newRow['S3-Strike'],
                                    'S3-Symbol': newRow['S3-Symbol'],
                                    'S3-Token': newRow['S3-Token'],
                                    'S4-Strike': newRow['S4-Strike'] || null,
                                    'S4-Symbol': newRow['S4-Symbol'] || null,
                                    'S4-Token': newRow['S4-Token'] || null,
                                };
                            })
                        );
                        setLockedPeSideData((prev) =>
                            prev.map((item, idx) => {
                                const newRow = responseData.PE_Results[idx];
                                return {
                                    ...item,
                                    'S3-Strike': newRow['S3-Strike'],
                                    'S3-Symbol': newRow['S3-Symbol'],
                                    'S3-Token': newRow['S3-Token'],
                                    'S4-Strike': newRow['S4-Strike'] || null,
                                    'S4-Symbol': newRow['S4-Symbol'] || null,
                                    'S4-Token': newRow['S4-Token'] || null,
                                };
                            })
                        );
                        const ceS3T = responseData.CE_Results.map((r) => r['S3-Token']).filter(Boolean);
                        const peS3T = responseData.PE_Results.map((r) => r['S3-Token']).filter(Boolean);
                        const ceS4T = responseData.CE_Results.map((r) => r['S4-Token']).filter(Boolean);
                        const peS4T = responseData.PE_Results.map((r) => r['S4-Token']).filter(Boolean);

                        newTokens = [...newTokens, ...ceS3T, ...peS3T, ...ceS4T, ...peS4T];
                        newTokens = [...new Set(newTokens)];
                        setCePeValuesOptions(newTokens);
                    }
                } else {
                    setCeSideData(responseData.CE_Results);
                    setPeSideData(responseData.PE_Results);
                    setCePeValuesOptions([...new Set(newTokens)]);
                }

                if (isUpdatingS3) {
                    setIsUpdatingS3(false);
                }
            }
        });

        // 3T updates
        socket.current.on('ce_3t_update', (partialCeData) => {
            setLastLtpTimestamp(Date.now());
            setRawLtpData((prev) => ({ ...prev, ...partialCeData }));
            setPendingLtpTokens((prevSet) => {
                if (prevSet.size === 0) return prevSet;
                const updated = new Set(prevSet);
                for (const tkn of Object.keys(partialCeData)) {
                    updated.delete(Number(tkn));
                }
                if (updated.size === 0) {
                    unfreezeUI();
                }
                return updated;
            });
        });
        socket.current.on('pe_3t_update', (partialPeData) => {
            setLastLtpTimestamp(Date.now());
            setRawLtpData((prev) => ({ ...prev, ...partialPeData }));
            setPendingLtpTokens((prevSet) => {
                if (prevSet.size === 0) return prevSet;
                const updated = new Set(prevSet);
                for (const tkn of Object.keys(partialPeData)) {
                    updated.delete(Number(tkn));
                }
                if (updated.size === 0) {
                    unfreezeUI();
                }
                return updated;
            });
        });

        // entry alert
        socket.current.on('entry_alert_signal', () => {
            setShouldPlaySound(true);
            LogManager.log('Alert', 'EntryAlert', 'Entry alert signal received');
            setTimeout(() => setShouldPlaySound(false), 5000);
        });

        // SEBI price-feed-stall alert (2026-08-06): server only emits this on
        // the exact tick that ends a >1min stall for the index THIS client has
        // selected, so just show whatever it sends — no extra filtering needed.
        socket.current.on('index_price_resumed', (data) => {
            if (data && data.index) {
                setStallAlert(data);
            }
        });

        // Rate-limit toggle (added 2026-04-27)
        // — server pushes state changes here so multiple open UI tabs stay synced.
        socket.current.on('rate_limit_state_update', (payload) => {
            if (payload && typeof payload.enabled === 'boolean') {
                setRateLimitEnabled(payload.enabled);
            }
        });
        // Seed initial state from server on connect (one HTTP call per mount).
        axios.get(`${baseUrl}/get_rate_limit`).then((r) => {
            if (r?.data && typeof r.data.enabled === 'boolean') {
                setRateLimitEnabled(r.data.enabled);
            }
        }).catch(() => { /* silent — toggle defaults to true if server unreachable */ });

        // 2026-05-11: KOTAK-only — also seed the local Check-Margin checkbox
        // from the server's _kite_margin_enabled flag. This closes the
        // checkbox ↔ server-flag sync loop we were missing on May 7. On
        // kite, no GET; /get_kite_margin only exists on kotak backend.
        if (getActiveBroker() === "kotak") {
            axios.get(`${baseUrl}/get_kite_margin`).then((r) => {
                if (r?.data && typeof r.data.enabled === 'boolean') {
                    setCheckMargin(r.data.enabled);
                    LogManager.log('Settings', 'KotakKiteMarginInitialSync', {
                        enabled: r.data.enabled, since: r.data.since
                    });
                }
            }).catch(() => { /* silent — checkbox default true if kotak unreachable */ });
        }

        return () => {
            if (socket.current) {
                socket.current.disconnect();
                socket.current = null;
            }
        };
    }, [isStrikeLockActive, isUpdatingS3]);

    // 4) Throttle LTP
    const updateLtpThrottled = throttle((latestRawData) => {
        setLtpData(latestRawData);
    }, 1000);

    useEffect(() => {
        updateLtpThrottled(rawLtpData);
    }, [rawLtpData]);

    // 5) Data staleness
    useEffect(() => {
        const checkDataStaleness = setInterval(() => {
            const now = Date.now();
            if (now - lastLtpTimestamp > 10000) {
                LogManager.log('App', 'StaleData', `No LTP for 10s => reconnect...`);
                if (socket.current) {
                    socket.current.disconnect();
                    socket.current.connect();
                }
                fetchData();
            }
        }, 5000);
        return () => clearInterval(checkDataStaleness);
    }, [lastLtpTimestamp]);

    // 6) Forced refresh
    useEffect(() => {
        const forcedRefreshInterval = setInterval(() => {
            toast.warning("Auto-refresh to prevent LTP hang...");
            LogManager.log('App', 'AutoRefresh', 'Auto-refresh triggered to prevent LTP hang');
            handleForceRefresh();
        }, 90 * 60 * 1000);
        return () => clearInterval(forcedRefreshInterval);
    }, []);

    // 7) Auto fetch data every 2s
    useEffect(() => {
        fetchData();
        const interval = setInterval(() => {
            fetchData();
        }, 2000);
        return () => clearInterval(interval);
    }, [
        instrument,
        expiryDate,
        lotSizes,
        ceSelectedStrikeInterval,
        peSelectedStrikeInterval,
        numRows,
        itms,
        peS4,
        ceS4
    ]);

    // 8) If we exit lock => fetch data
    useEffect(() => {
        if (!isStrikeLockActive) {
            fetchData();
        }
    }, [isStrikeLockActive]);

    // 9) Clock
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 2000);
        return () => clearInterval(timer);
    }, []);

    // 10) Distances blink and distance change detection
    useEffect(() => {
        // PE Side distance change detection
        if (data?.PE_SIDE?.Default_Required_Distance !== undefined) {
            if (prevPeRequiredDistance !== null) {
                if (prevPeRequiredDistance !== data.PE_SIDE.Default_Required_Distance) {
                    setPeRequiredDistanceTimestamp(new Date());
                    const diff = data.PE_SIDE.Default_Required_Distance - prevPeRequiredDistance;
                    setPeDistanceChange(diff);
                    setPeBlink(true);
                    setTimeout(() => setPeBlink(false), 14000);

                    // Check if we have any active movements and handle distance changes
                    handleDistanceChange(data.PE_SIDE.Default_Required_Distance, prevCeRequiredDistance);

                    LogManager.log('Market Update', 'PE Side Distance Changed', {
                        previous_distance: prevPeRequiredDistance,
                        new_distance: data.PE_SIDE.Default_Required_Distance,
                        change_amount: diff,
                    });
                } else {
                    setPeDistanceChange(null);
                }
            } else {
                setPeRequiredDistanceTimestamp(new Date());
            }
            setPrevPeRequiredDistance(data.PE_SIDE.Default_Required_Distance);
        }

        // CE Side distance change detection
        if (data?.CE_SIDE?.Default_Required_Distance !== undefined) {
            if (prevCeRequiredDistance !== null) {
                if (prevCeRequiredDistance !== data.CE_SIDE.Default_Required_Distance) {
                    setCeRequiredDistanceTimestamp(new Date());
                    const diff = data.CE_SIDE.Default_Required_Distance - prevCeRequiredDistance;
                    setCeDistanceChange(diff);
                    setCeBlink(true);
                    setTimeout(() => setCeBlink(false), 14000);

                    // Check if we have any active movements and handle distance changes
                    handleDistanceChange(prevPeRequiredDistance, data.CE_SIDE.Default_Required_Distance);

                    LogManager.log('Market Update', 'CE Side Distance Changed', {
                        previous_distance: prevCeRequiredDistance,
                        new_distance: data.CE_SIDE.Default_Required_Distance,
                        change_amount: diff,
                    });
                } else {
                    setCeDistanceChange(null);
                }
            } else {
                setCeRequiredDistanceTimestamp(new Date());
            }
            setPrevCeRequiredDistance(data.CE_SIDE.Default_Required_Distance);
        }
    }, [data]);

    // 11) Entry alerts
    useEffect(() => {
        if (socket.current) {
            if (peEntryAlert || ceEntryAlert) {
                if (instrument && expiryDate) {
                    socket.current.emit('entry_alert_request', {
                        instrument,
                        expiry: expiryDate?.toISOString().split("T")[0],
                        num_rows: numRows,
                        pe_alert_distance: peAlertDistance,
                        ce_alert_distance: ceAlertDistance,
                        pe_alert_on: peEntryAlert,
                        ce_alert_on: ceEntryAlert
                    });

                    LogManager.log('Alert', 'SetupEntryAlert', {
                        pe_alert: peEntryAlert,
                        ce_alert: ceEntryAlert,
                        pe_distance: peAlertDistance,
                        ce_distance: ceAlertDistance
                    });
                }
            } else {
                socket.current.emit('entry_alert_stop');
                LogManager.log('Alert', 'StopEntryAlert', 'Entry alerts stopped');
            }
        }
    }, [peEntryAlert, ceEntryAlert, peAlertDistance, ceAlertDistance, instrument, expiryDate, numRows]);

    // 12) Token preservation for move operations
    useEffect(() => {
        ensureMoveTokensSubscribed();
    }, [movingRows, activeMoving]);

    // 13) Periodic token reconciliation for move operations
    useEffect(() => {
        const interval = setInterval(() => {
            reconcileMovementTokens();
        }, 10000); // Every 10 seconds

        return () => clearInterval(interval);
    }, [peSideData, ceSideData, movingRows, activeMoving]);

    // 14) Ensure token preservation when distance changes
    useEffect(() => {
        const distanceChanged =
            (prevPeRequiredDistance !== null && prevPeRequiredDistance !== data?.PE_SIDE?.Default_Required_Distance) ||
            (prevCeRequiredDistance !== null && prevCeRequiredDistance !== data?.CE_SIDE?.Default_Required_Distance);

        if (distanceChanged && (Object.keys(movingRows).length > 0 || Object.keys(activeMoving).length > 0)) {
            LogManager.log('Move', 'DistanceChanged', 'Distance changed - ensuring token subscriptions');
            ensureMoveTokensSubscribed();
            preserveTokensWithBackend();
        }
    }, [data?.PE_SIDE?.Default_Required_Distance, data?.CE_SIDE?.Default_Required_Distance]);

    // ===== NEW DECIMAL PNL MONITORING FOR SINGLE ROWS WITH 10MS CONTINUOUS MATCH =====
    useEffect(() => {
        if (Object.keys(activeMoving).length === 0 || !ltpData) return;

        Object.keys(activeMoving).forEach(rowKey => {
            const side = rowKey.split('-')[0];

            if (pairedMoves[rowKey]) return; // Skip if part of a paired move

            const executionData = executedQuantities[rowKey] || { pending: 0 };
            const currentBatchKey = `${rowKey}-batch-${executionData.processedBatches || 0}`;

            if (executionData.pending <= 0 || continuousMatchIntervals.current[currentBatchKey]) {
                return; // Skip if all done or already tracking
            }

            const capturedData = capturedDistances[rowKey];
            if (!capturedData || !capturedData.sourceRow || !capturedData.targetRow) {
                LogManager.log('Trading Move', 'Error', `Missing data for move operation on ${rowKey}`);
                return;
            }

            LogManager.log('Trading Move', 'Starting 10ms Continuous Check', {
                move_identifier: rowKey,
                from_strike: capturedData.sourceRow.s1Strike,
                to_strike: capturedData.targetRow.s1Strike,
                batch_number: (executionData.processedBatches || 0) + 1,
                pending_quantity: executionData.pending,
            });

            let consecutiveMatches = 0;
            continuousMatchIntervals.current[currentBatchKey] = setInterval(() => {
                const targetPnlAdjustment = parseFloat(movePnlValues[rowKey] || 0.05);

                const latestCurrentPnl = calculateCurrentPnLFromPreserved(capturedData.sourceRow, side, ltpData, capturedData.modeState);
                const latestTargetPnl = calculateCurrentPnLFromPreserved(capturedData.targetRow, side, ltpData, capturedData.modeState);

                if (latestCurrentPnl === null || latestTargetPnl === null) {
                    if (consecutiveMatches > 0) {
                        LogManager.log('Trading Move', 'Match Reset - PnL Calculation Failed (Missing LTP)', {
                            move_identifier: rowKey,
                            batch_number: (executionData.processedBatches || 0) + 1
                        });
                    }
                    consecutiveMatches = 0;
                    return;
                }

                const latestDecimalCurrentPnl = parseFloat(latestCurrentPnl.toFixed(2));
                const latestDecimalTargetPnl = parseFloat(latestTargetPnl.toFixed(2));

                const preservedValues = preservedPnlValues[rowKey] || {};
                let latestRequiredPnl = distanceChangedDuringMove[rowKey] && preservedValues.currentPnl !== undefined ?
                    parseFloat(preservedValues.currentPnl.toFixed(2)) + targetPnlAdjustment :
                    latestDecimalCurrentPnl + targetPnlAdjustment;
                latestRequiredPnl = parseFloat(latestRequiredPnl.toFixed(2));

                const stillMatched = latestDecimalTargetPnl >= latestRequiredPnl;

                const logDetails = {
                    move_identifier: rowKey,
                    batch_number: (executionData.processedBatches || 0) + 1,
                    from_strike: capturedData.sourceRow.s1Strike,
                    to_strike: capturedData.targetRow.s1Strike,
                    current_pnl: latestDecimalCurrentPnl,
                    target_pnl: latestDecimalTargetPnl,
                    required_pnl: latestRequiredPnl,
                    current_ltp: ltpData[capturedData.sourceRow.s1Token]?.toFixed(2),
                    target_ltp: ltpData[capturedData.targetRow.s1Token]?.toFixed(2),
                };

                if (stillMatched) {
                    consecutiveMatches++;
                    LogManager.log('Trading Move', `Millisecond Match ${consecutiveMatches}/25`, logDetails);
                    //millisecond change to 25
                    if (consecutiveMatches >= 25) {
                        clearInterval(continuousMatchIntervals.current[currentBatchKey]);
                        delete continuousMatchIntervals.current[currentBatchKey];

                        LogManager.log('Trading Move', '25ms Continuous Match Achieved - Executing Batch', {
                            ...logDetails,
                            from_position_details: {
                                s1_ltp: ltpData[capturedData.sourceRow.s1Token]?.toFixed(2) || 'N/A',
                                s2_ltp: ltpData[capturedData.sourceRow.s2Token]?.toFixed(2) || 'N/A',
                                s3_ltp: ltpData[capturedData.sourceRow.s3Token]?.toFixed(2) || 'N/A',
                                s4_ltp: ltpData[capturedData.sourceRow.s4Token]?.toFixed(2) || 'N/A',
                            },
                            to_position_details: {
                                s1_ltp: ltpData[capturedData.targetRow.s1Token]?.toFixed(2) || 'N/A',
                                s2_ltp: ltpData[capturedData.targetRow.s2Token]?.toFixed(2) || 'N/A',
                                s3_ltp: ltpData[capturedData.targetRow.s3Token]?.toFixed(2) || 'N/A',
                                s4_ltp: ltpData[capturedData.targetRow.s4Token]?.toFixed(2) || 'N/A',
                            },
                        });

                        executeMoveTrade(rowKey, capturedData, executionData);
                    }
                } else {
                    if (consecutiveMatches > 0) {
                        LogManager.log('Trading Move', 'Match Reset - Condition No Longer Met', {
                            ...logDetails,
                            reset_at_millisecond: consecutiveMatches,
                        });
                    }
                    consecutiveMatches = 0;
                }
            }, 1);
        });

        return () => {
            Object.values(continuousMatchIntervals.current).forEach(clearInterval);
            continuousMatchIntervals.current = {};
        };
    }, [ltpData, activeMoving, executedQuantities]);

    // ===== NEW DECIMAL PNL MONITORING FOR PAIRED MOVES WITH 10MS CONTINUOUS MATCH =====
    useEffect(() => {
        if (!ltpData || Object.keys(pairedMoves).length === 0) return;

        const processedPairs = new Set();

        Object.keys(pairedMoves).forEach(key1 => {
            const key2 = pairedMoves[key1];
            if (!activeMoving[key1] || !activeMoving[key2]) return;

            const pairId = [key1, key2].sort().join('-');
            if (processedPairs.has(pairId)) return;
            processedPairs.add(pairId);

            const executionData1 = executedQuantities[key1] || { pending: 0 };
            const executionData2 = executedQuantities[key2] || { pending: 0 };
            const currentBatchKey = `${pairId}-batch-${executionData1.processedBatches || 0}`;

            if (executionData1.pending <= 0 || executionData2.pending <= 0 || continuousMatchIntervals.current[currentBatchKey]) {
                return; // Skip if done or already tracking
            }

            const capturedData1 = capturedDistances[key1];
            const capturedData2 = capturedDistances[key2];
            const side = key1.split('-')[0];

            if (!capturedData1 || !capturedData1.sourceRow || !capturedData1.targetRow ||
                !capturedData2 || !capturedData2.sourceRow || !capturedData2.targetRow) {
                LogManager.log('Trading Move', 'Error', `Missing data for paired move ${pairId}`);
                return;
            }

            LogManager.log('Trading Move', 'Starting 25ms Continuous Check for Paired Move', {
                pair_identifier: pairId,
                batch_number: (executionData1.processedBatches || 0) + 1,
            });

            let consecutiveMatches = 0;
            continuousMatchIntervals.current[currentBatchKey] = setInterval(() => {
                const adj1 = parseFloat(movePnlValues[key1] || '0.05');
                const adj2 = parseFloat(movePnlValues[key2] || '0.05');

                const latestPnl1 = calculateCurrentPnLFromPreserved(capturedData1.sourceRow, side, ltpData, capturedData1.modeState);
                const latestPnl2 = calculateCurrentPnLFromPreserved(capturedData2.sourceRow, side, ltpData, capturedData2.modeState);
                const latestTPnl1 = calculateCurrentPnLFromPreserved(capturedData1.targetRow, side, ltpData, capturedData1.modeState);
                const latestTPnl2 = calculateCurrentPnLFromPreserved(capturedData2.targetRow, side, ltpData, capturedData2.modeState);

                if (latestPnl1 === null || latestPnl2 === null || latestTPnl1 === null || latestTPnl2 === null) {
                    if (consecutiveMatches > 0) {
                        LogManager.log('Trading Move', 'Paired Match Reset - PnL Calculation Failed', {
                            pair_identifier: pairId,
                            batch_number: (executionData1.processedBatches || 0) + 1,
                        });
                    }
                    consecutiveMatches = 0;
                    return;
                }

                const latestDecimal1 = parseFloat(latestPnl1.toFixed(2));
                const latestDecimal2 = parseFloat(latestPnl2.toFixed(2));
                const latestDecimalT1 = parseFloat(latestTPnl1.toFixed(2));
                const latestDecimalT2 = parseFloat(latestTPnl2.toFixed(2));

                const latestCurrentSum = parseFloat((latestDecimal1 + latestDecimal2).toFixed(2));
                const latestTargetSum = parseFloat((latestDecimalT1 + latestDecimalT2).toFixed(2));

                const preservedPair1 = preservedPnlValues[key1] || {};
                const preservedPair2 = preservedPnlValues[key2] || {};

                const usePreserved = (distanceChangedDuringMove[key1] || distanceChangedDuringMove[key2]) &&
                    preservedPair1.currentPnl !== undefined && preservedPair2.currentPnl !== undefined;

                let latestRequiredSum = usePreserved ?
                    parseFloat((preservedPair1.currentPnl + preservedPair2.currentPnl + adj1 + adj2).toFixed(2)) :
                    parseFloat((latestCurrentSum + adj1 + adj2).toFixed(2));

                const stillMatched = latestTargetSum >= latestRequiredSum;

                const logDetails = {
                    pair_identifier: pairId,
                    batch_number: (executionData1.processedBatches || 0) + 1,
                    current_combined_pnl: latestCurrentSum,
                    target_combined_pnl: latestTargetSum,
                    required_combined_pnl: latestRequiredSum,
                };

                if (stillMatched) {
                    consecutiveMatches++;
                    LogManager.log('Trading Move', `Paired Millisecond Match ${consecutiveMatches}/25`, logDetails);

                    if (consecutiveMatches >= 25) {
                        clearInterval(continuousMatchIntervals.current[currentBatchKey]);
                        delete continuousMatchIntervals.current[currentBatchKey];

                        LogManager.log('Trading Move', '25ms Continuous Match Achieved - Executing Paired Batch', logDetails);

                        executePairedMoveTrade(key1, key2, capturedData1, capturedData2, executionData1, executionData2);
                    }
                } else {
                    if (consecutiveMatches > 0) {
                        LogManager.log('Trading Move', 'Paired Match Reset - Condition No Longer Met', {
                            ...logDetails,
                            reset_at_millisecond: consecutiveMatches,
                        });
                    }
                    consecutiveMatches = 0;
                }
            }, 1);
        });

        return () => {
            Object.values(continuousMatchIntervals.current).forEach(clearInterval);
            continuousMatchIntervals.current = {};
        };
    }, [ltpData, pairedMoves, activeMoving, executedQuantities]);

    // -------------------- FREEZE UI --------------------
    const freezeUI = () => {
        setIsFreezed(true);
        clearTimeout(freezeTimerRef.current);
        freezeTimerRef.current = setTimeout(() => {
            unfreezeUI(true);
        }, 4000);
    };

    const unfreezeUI = (delayed = false) => {
        clearTimeout(freezeTimerRef.current);
        setIsFreezed(false);
        setPendingLtpTokens(new Set());
        if (delayed) {
            toast.info("Data updated; unfreezing UI.");
        }
    };

    // -------------------- DATA UTILS --------------------
    const fetchExpiryDates = async () => {
        try {
            const response = await axios.get(`${baseUrl}/filter_expiry_dates`);
            const respData = response.data;
            const parsedData = {};

            // Loop through each instrument in the response
            for (let key in respData) {
                let dateStrings = [];

                // If the response is an object with an "expiryDates" key, use that.
                if (
                    typeof respData[key] === 'object' &&
                    respData[key] !== null &&
                    Array.isArray(respData[key].expiryDates)
                ) {
                    dateStrings = respData[key].expiryDates;
                    // If instrument parameters are provided (e.g., lotSizes), update state accordingly.
                    if (
                        modalInstrument === key &&
                        respData[key].instrumentParams &&
                        respData[key].instrumentParams.lotSizes
                    ) {
                        setLotSizes(respData[key].instrumentParams.lotSizes);
                    }
                } else if (typeof respData[key] === 'string') {
                    // Otherwise, assume the response is a string containing dates
                    dateStrings = respData[key].match(/\d{4}-\d{2}-\d{2}/g) || [];
                }
                // Convert strings to Date objects and sort them
                parsedData[key] = dateStrings.map(str => new Date(str)).sort((a, b) => a - b);
            }
            setExpiryDates(parsedData);
            if (parsedData[modalInstrument] && parsedData[modalInstrument].length > 0) {
                setModalExpiryDate(parsedData[modalInstrument][0]);
            }

            LogManager.log('Data', 'FetchedExpiryDates', { instruments: Object.keys(parsedData) });
        } catch (error) {
            LogManager.log('Error', 'FetchExpiryDates', `Failed to fetch expiry dates: ${error.message}`);
            toast.error("Failed to fetch expiry dates.");
        }
    };

    const fetchData = () => {
        if (!instrument || !expiryDate || lotSizes.length < 1) return;

        requestIdRef.current += 1;
        const currentReqId = requestIdRef.current;

        const params = {
            date: expiryDate?.toISOString().split("T")[0],
            index_id: instrumentIdMapping[instrument],
            num_rows: numRows,
            requestId: currentReqId,
            num_itms: itms,
            pe_s4: peS4,
            ce_s4: ceS4,
        };
        if (ceSelectedStrikeInterval !== null) {
            params.ce_strike_interval = ceSelectedStrikeInterval;
        }
        if (peSelectedStrikeInterval !== null) {
            params.pe_strike_interval = peSelectedStrikeInterval;
        }

        if (socket.current) {
            socket.current.emit('data_request', params);
        }
    };

    const handleForceRefresh = () => {
        fetchData();
        if (socket.current) {
            socket.current.disconnect();
            socket.current.connect();
        }
    };

    // -------------------- ENHANCED MOVE FUNCTIONALITY --------------------

    // Function to check if a row can be paired with another row
    const canPairMRows = (rowKey) => {
        const side = rowKey.split('-')[0]; // PE or CE
        const strikeVal = parseInt(rowKey.split('-')[1]);
        const sideData = side === 'PE' ? peSideData : ceSideData;

        // Find current row index
        const currentRowIndex = sideData.findIndex(r => String(r['S1-Strike']) === String(strikeVal));
        if (currentRowIndex === -1) return null;

        // Check row above (currentRowIndex - 1)
        if (currentRowIndex > 0) {
            const aboveRowStrike = sideData[currentRowIndex - 1]['S1-Strike'];
            const aboveRowKey = `${side}-${aboveRowStrike}`;
            if (movingRows[aboveRowKey]) {
                return { key: aboveRowKey, direction: 'above' };
            }
        }

        // Check row below (currentRowIndex + 1)
        if (currentRowIndex < sideData.length - 1) {
            const belowRowStrike = sideData[currentRowIndex + 1]['S1-Strike'];
            const belowRowKey = `${side}-${belowRowStrike}`;
            if (movingRows[belowRowKey]) {
                return { key: belowRowKey, direction: 'below' };
            }
        }

        // Check if this row is part of a selected checkbox group
        if ((side === 'PE' ? peSelectedRows : ceSelectedRows).includes(rowKey)) {
            // Find other selected rows that are adjacent
            const selectedRows = (side === 'PE' ? peSelectedRows : ceSelectedRows);
            for (const selectedKey of selectedRows) {
                if (selectedKey === rowKey) continue;

                const selectedStrike = parseInt(selectedKey.split('-')[1]);
                const selectedRowIndex = sideData.findIndex(r => String(r['S1-Strike']) === String(selectedStrike));

                // Check if adjacent (directly above or below)
                if (Math.abs(currentRowIndex - selectedRowIndex) === 1) {
                    return { key: selectedKey, direction: currentRowIndex > selectedRowIndex ? 'above' : 'below' };
                }
            }
        }

        return null;
    };

    // Enhanced calculation function using preserved data
    const calculateCurrentPnLFromPreserved = (preservedRowData, side, ltpData, modeState) => {
        if (!preservedRowData || !ltpData) return null;

        // Extract tokens from preserved data
        const s1Token = preservedRowData.s1Token;
        const s2Token = preservedRowData.s2Token;
        const s3Token = preservedRowData.s3Token;
        const s4Token = preservedRowData.s4Token;

        // Check if LTPs are available
        if (!ltpData[s1Token] || !ltpData[s2Token] || !ltpData[s3Token]) {
            return null; // Can't calculate PnL without all prices
        }

        // Get LTP values
        const s1Ltp = ltpData[s1Token];
        const s2Ltp = ltpData[s2Token];
        const s3Ltp = ltpData[s3Token];

        // Use mode state from when the move was activated
        const useS4 = modeState?.useS4 || false;
        const s3Multiplier = modeState?.s3Multiplier || 1;

        // Calculate PnL based on preserved mode
        if (useS4 && s4Token && ltpData[s4Token]) {
            const s4Ltp = ltpData[s4Token];
            return (s2Ltp + s3Ltp + s4Ltp) - s1Ltp;
        } else {
            return (s2Ltp + (s3Ltp * s3Multiplier)) - s1Ltp;
        }
    };


    // This function will directly calculate PnL given the tokens and current LTP data
    // (Original PnL calculation function - keeping for compatibility)
    const calculateCurrentPnL = (rowData, side, ltpData) => {
        if (!rowData || !ltpData) return 0;

        const s1Ltp = ltpData[rowData['S1-Token']] || 0.0;
        const s2Ltp = ltpData[rowData['S2-Token']] || 0.0;
        const s3Ltp = ltpData[rowData['S3-Token']] || 0.0;

        const hasS4 = rowData['S4-Token'] && rowData['S4-Strike'];
        const s4Ltp = hasS4 ? (ltpData[rowData['S4-Token']] || 0.0) : 0.0;

        // Use effective multiplier only when T3 is selected
        const useS4 = (side === 'PE' ? peS4 : ceS4) && hasS4;
        const effectiveMultiplier = useS4 ? 1 : (side === 'PE' ? peS3Multiplier : ceS3Multiplier);

        const pnl = useS4 ?
            (s2Ltp + s3Ltp + s4Ltp) - s1Ltp :
            (s2Ltp + (s3Ltp * effectiveMultiplier)) - s1Ltp;

        return pnl;
    };


    // Create a comprehensive distance change handler
    const handleDistanceChange = (newPeDistance, newCeDistance) => {
        // If no active movements, nothing to do
        if (Object.keys(movingRows).length === 0 && Object.keys(activeMoving).length === 0) return;

        LogManager.log('Trading Move', 'Market Distance Changed', {
            PE_distance: {
                previous: prevPeRequiredDistance,
                current: newPeDistance,
                change: newPeDistance - (prevPeRequiredDistance || 0)
            },
            CE_distance: {
                previous: prevCeRequiredDistance,
                current: newCeDistance,
                change: newCeDistance - (prevCeRequiredDistance || 0)
            },
            active_moves_affected: Object.keys({ ...movingRows, ...activeMoving }).length,
        });

        // Update each active or pending movement
        Object.keys({ ...movingRows, ...activeMoving }).forEach(rowKey => {
            const side = rowKey.split('-')[0]; // PE or CE
            const capturedData = capturedDistances[rowKey];
            if (!capturedData) return;

            // Determine if this row's side had a distance change
            const distanceChanged = (side === 'PE' && prevPeRequiredDistance !== newPeDistance) ||
                (side === 'CE' && prevCeRequiredDistance !== newCeDistance);

            if (distanceChanged) {
                // Mark this row as having experienced a distance change
                setDistanceChangedDuringMove(prev => ({
                    ...prev,
                    [rowKey]: true
                }));

                // Preserve current PnL values if not already preserved for this move
                if (!preservedPnlValues[rowKey]) {
                    const currentPnl = calculateCurrentPnLFromPreserved(capturedData.sourceRow, side, ltpData, capturedData.modeState);
                    const targetPnl = calculateCurrentPnLFromPreserved(capturedData.targetRow, side, ltpData, capturedData.modeState);

                    if (currentPnl !== null && targetPnl !== null) {
                        setPreservedPnlValues(prev => ({
                            ...prev,
                            [rowKey]: {
                                timestamp: Date.now(),
                                currentPnl,
                                targetPnl,
                                requestedAdjustment: parseFloat(movePnlValues[rowKey] || 0.05)
                            }
                        }));

                        LogManager.log('Trading Move', 'Profit Values Preserved Due to Distance Change', {
                            move_identifier: rowKey,
                            from_strike: capturedData.sourceRow.s1Strike,
                            to_strike: capturedData.targetRow ? capturedData.targetRow.s1Strike : 'Not set yet',
                            preserved_current_pnl: currentPnl,
                            preserved_target_pnl: targetPnl,
                            reason: 'Market distance changed during active move',
                        });
                    }
                }

                updateMoveStatus(rowKey, 'warning', 'Distance changed');
            }
        });
    };


    // Function to update move operation status
    const updateMoveStatus = (rowKey, status, message = null) => {
        setMoveOperationStatus(prev => ({
            ...prev,
            [rowKey]: { status, message, timestamp: Date.now() }
        }));

        const statusMessages = {
            'info': 'Information',
            'warning': 'Warning',
            'error': 'Error',
            'success': 'Success'
        };

        LogManager.log('Trading Move', statusMessages[status] || 'Status Update', {
            move_identifier: rowKey,
            status_type: status,
            message: message || status,
        });
    };

    // Helper function to create order sequence from preserved row data
    const createOrderSequenceFromPreserved = (preservedRow, selectedAction, side) => {
        const s1 = preservedRow.s1Symbol;
        const s2 = preservedRow.s2Symbol;
        const s3 = preservedRow.s3Symbol;
        const s4 = preservedRow.s4Symbol; // might be undefined

        let orderSequence = [];
        if (selectedAction === '+') {
            // BUY S1, SELL S2, S3, S4
            orderSequence.push({ tradingsymbol: s1, transaction_type: "BUY" });
            orderSequence.push({ tradingsymbol: s2, transaction_type: "SELL" });
            orderSequence.push({ tradingsymbol: s3, transaction_type: "SELL" });
            if (s4) {
                orderSequence.push({ tradingsymbol: s4, transaction_type: "SELL" });
            }
        } else {
            // EXIT => depends on exitMethod
            const exitMethod = (side === 'CE') ? ceExitMethod : peExitMethod;
            if (exitMethod === 'S2') {
                // S2->S3->S4 => BUY, then SELL S1
                if (s2) orderSequence.push({ tradingsymbol: s2, transaction_type: "BUY" });
                if (s3) orderSequence.push({ tradingsymbol: s3, transaction_type: "BUY" });
                if (s4) orderSequence.push({ tradingsymbol: s4, transaction_type: "BUY" });
                if (s1) orderSequence.push({ tradingsymbol: s1, transaction_type: "SELL" });
            } else {
                // S3->S4->S2 => BUY, then SELL S1
                if (s3) orderSequence.push({ tradingsymbol: s3, transaction_type: "BUY" });
                if (s4) orderSequence.push({ tradingsymbol: s4, transaction_type: "BUY" });
                if (s2) orderSequence.push({ tradingsymbol: s2, transaction_type: "BUY" });
                if (s1) orderSequence.push({ tradingsymbol: s1, transaction_type: "SELL" });
            }
        }
        return orderSequence;
    };

    // Extract execution logic to a separate function
    const executeMoveTrade = (rowKey, capturedData, executionData) => {
        const side = rowKey.split('-')[0];
        const selectedLotSize = selectedLotSizesForMove[rowKey];

        if (!selectedLotSize) {
            LogManager.log('Trading Move', 'Execution Aborted - No Quantity', { move_identifier: rowKey });
            return;
        }

        const maxQty = maxOrderQuantities[instrument] || 1800;
        const batchSize = Math.min(executionData.pending, maxQty);

        const sourceRow = capturedData.sourceRow;
        const targetRow = capturedData.targetRow;

        if (!sourceRow || !targetRow) {
            LogManager.log('Trading Move', 'Execution Aborted - Missing Data', { move_identifier: rowKey });
            return;
        }

        const closeOrderSeq = createOrderSequenceFromPreserved(sourceRow, '-', side);
        const placeOrderSeq = createOrderSequenceFromPreserved(targetRow, '+', side);
        const getExchangeType = (instr) => instr === "CRUDEOIL" ? "MCX" : (instr === "SENSEX" || instr === "BANKEX") ? "BFO" : "NFO";   // 2026-05-14: MCX route

        const createOrders = (seq, baseQty, rowData, modeState) => {
            return seq.map(o => {
                const multiplier = modeState?.s3Multiplier || 1;
                const isS3 = (o.tradingsymbol === rowData.s3Symbol);
                const realQty = isS3 ? baseQty * multiplier : baseQty;
                return {
                    tradingsymbol: o.tradingsymbol,
                    direction: o.transaction_type.toUpperCase(),
                    quantity: realQty,
                    exchangeType: getExchangeType(instrument),
                    selectedindex: instrument,
                };
            });
        };

        const closeOrders = createOrders(closeOrderSeq, batchSize, sourceRow, capturedData.modeState);
        const placeOrders = createOrders(placeOrderSeq, batchSize, targetRow, capturedData.modeState);

        // Update execution quantities BEFORE API call
        setExecutedQuantities(prev => ({
            ...prev,
            [rowKey]: {
                ...executionData,
                executed: executionData.executed + batchSize,
                pending: executionData.pending - batchSize,
                processedBatches: (executionData.processedBatches || 0) + 1,
            }
        }));

        // Execute orders
        executeCloseAndPlaceOrder(closeOrders, placeOrders, `single-${rowKey}`)
            .then(result => {
                const updatedExecutionData = { ...executedQuantities[rowKey] };
                updatedExecutionData.pending -= batchSize;
                updatedExecutionData.processedBatches += 1;

                if (result.success) {
                    toast.success(`${rowKey}: Move batch ${updatedExecutionData.processedBatches} executed! (${batchSize}/${selectedLotSize})`);
                    LogManager.log('Trading Move', 'Batch Execution Successful', {
                        move_identifier: rowKey,
                        batch_number: updatedExecutionData.processedBatches,
                        remaining_quantity: updatedExecutionData.pending,
                    });

                    if (updatedExecutionData.pending <= 0) {
                        setActiveMoving(prev => {
                            const newActive = { ...prev };
                            delete newActive[rowKey];
                            return newActive;
                        });
                        LogManager.log('Trading Move', 'Position Move Completed', {
                            move_identifier: rowKey,
                            total_quantity_moved: selectedLotSize,
                        });
                        toast.success(`${rowKey}: All quantity moved successfully!`);
                    }
                } else {
                    toast.error(`${rowKey}: Failed to execute batch - ${result.error}`);
                    LogManager.log('Trading Move', 'Batch Execution Failed', {
                        move_identifier: rowKey,
                        batch_number: updatedExecutionData.processedBatches,
                        error_message: result.error,
                    });

                    // Rollback quantities on failure
                    setExecutedQuantities(prev => ({
                        ...prev,
                        [rowKey]: executionData, // Revert to pre-call state
                    }));
                }
            });
    };

    // Function for paired move execution
    const executePairedMoveTrade = (key1, key2, capturedData1, capturedData2, executionData1, executionData2) => {
        const side = key1.split('-')[0];
        const lotSize1 = selectedLotSizesForMove[key1];
        const lotSize2 = selectedLotSizesForMove[key2];

        if (!lotSize1 || !lotSize2) {
            LogManager.log('Trading Move', 'Execution Aborted - No Quantity (Paired)', { pair_identifier: `${key1}-${key2}` });
            return;
        }

        const maxQty = maxOrderQuantities[instrument] || 1800;
        const batchSize1 = Math.min(executionData1.pending, maxQty);
        const batchSize2 = Math.min(executionData2.pending, maxQty);

        const getExchangeType = (instr) => instr === "CRUDEOIL" ? "MCX" : (instr === "SENSEX" || instr === "BANKEX") ? "BFO" : "NFO";   // 2026-05-14: MCX route

        const createOrders = (seq, baseQty, rowData, modeState) => {
            return seq.map(o => {
                const multiplier = modeState?.s3Multiplier || 1;
                const isS3 = (o.tradingsymbol === rowData.s3Symbol);
                const realQty = isS3 ? baseQty * multiplier : baseQty;
                return {
                    tradingsymbol: o.tradingsymbol,
                    direction: o.transaction_type.toUpperCase(),
                    quantity: realQty,
                    exchangeType: getExchangeType(instrument),
                    selectedindex: instrument,
                };
            });
        };

        let allCloseOrders = [
            ...createOrders(createOrderSequenceFromPreserved(capturedData1.sourceRow, '-', side), batchSize1, capturedData1.sourceRow, capturedData1.modeState),
            ...createOrders(createOrderSequenceFromPreserved(capturedData2.sourceRow, '-', side), batchSize2, capturedData2.sourceRow, capturedData2.modeState)
        ];

        let allPlaceOrders = [
            ...createOrders(createOrderSequenceFromPreserved(capturedData1.targetRow, '+', side), batchSize1, capturedData1.targetRow, capturedData1.modeState),
            ...createOrders(createOrderSequenceFromPreserved(capturedData2.targetRow, '+', side), batchSize2, capturedData2.targetRow, capturedData2.modeState)
        ];

        // Update execution counts BEFORE API call
        setExecutedQuantities(prev => ({
            ...prev,
            [key1]: { ...executionData1, executed: executionData1.executed + batchSize1, pending: executionData1.pending - batchSize1, processedBatches: (executionData1.processedBatches || 0) + 1 },
            [key2]: { ...executionData2, executed: executionData2.executed + batchSize2, pending: executionData2.pending - batchSize2, processedBatches: (executionData2.processedBatches || 0) + 1 }
        }));

        // Execute orders
        executeCloseAndPlaceOrder(allCloseOrders, allPlaceOrders, `paired-${key1}-${key2}`)
            .then(result => {
                const updatedExec1 = { ...executedQuantities[key1] };
                const updatedExec2 = { ...executedQuantities[key2] };
                updatedExec1.pending -= batchSize1;
                updatedExec2.pending -= batchSize2;
                updatedExec1.processedBatches += 1;
                updatedExec2.processedBatches += 1;

                if (result.success) {
                    toast.success(`Paired move batch executed for ${key1} & ${key2}!`);
                    LogManager.log('Trading Move', 'Paired Batch Execution Successful', {
                        pair_identifier: `${key1}-${key2}`,
                        batch_number: updatedExec1.processedBatches,
                    });

                    if (updatedExec1.pending <= 0 && updatedExec2.pending <= 0) {
                        setActiveMoving(prev => {
                            const newActive = { ...prev };
                            delete newActive[key1];
                            delete newActive[key2];
                            return newActive;
                        });
                        LogManager.log('Trading Move', 'Paired Position Move Completed', {
                            pair_identifier: `${key1}-${key2}`,
                        });
                        toast.success(`Paired move complete for ${key1} and ${key2}!`);
                    }
                } else {
                    toast.error(`Paired move failed: ${result.error}`);
                    LogManager.log('Trading Move', 'Paired Batch Execution Failed', {
                        pair_identifier: `${key1}-${key2}`,
                        error_message: result.error,
                    });
                    // Rollback
                    setExecutedQuantities(prev => ({
                        ...prev,
                        [key1]: executionData1,
                        [key2]: executionData2,
                    }));
                }
            });
    };

    // Centralized function to send orders to the backend
    const executeCloseAndPlaceOrder = async (closeOrders, placeOrders, moveIdentifier) => {
        try {
            const combinedOrders = [...closeOrders, ...placeOrders];

            LogManager.log('Trading API', 'Sending Combined Order Request', {
                move_identifier: moveIdentifier,
                total_orders: combinedOrders.length,
                order_details: combinedOrders
            });

            // Send combined orders — use iceberg endpoint when toggle is ON
            const orderEndpoint = icebergEnabled ? '/iceberg_orders' : '/preprocess_orders';
            const response = await axios.post(`${baseUrl}${orderEndpoint}`, {
                orders: combinedOrders,
                move_position: icebergEnabled ? false : true, // iceberg doesn't use move_position
                market_protection: marketProtection || 30,
            });

            LogManager.log('Trading API', 'Order Execution Response [Success]', {
                move_identifier: moveIdentifier,
                response_data: response.data
            });

            return {
                success: true,
                response: response.data
            };
        } catch (error) {
            const errorMessage = error.response?.data?.error || error.message || "Unknown error";
            LogManager.log('Trading API', 'Order Execution Response [Failure]', {
                move_identifier: moveIdentifier,
                error_message: errorMessage,
                error_details: error.response?.data || 'No additional details'
            });

            return {
                success: false,
                error: errorMessage
            };
        }
    };

    // Enhanced token subscription management
    const ensureMoveTokensSubscribed = () => {
        // Collect all tokens that need to remain subscribed
        const allRequiredTokens = new Set();

        // Process each active and pending movement
        Object.keys({ ...movingRows, ...activeMoving }).forEach(rowKey => {
            const capturedData = capturedDistances[rowKey];
            if (!capturedData) return;

            // Add source and target row tokens
            ['sourceRow', 'targetRow'].forEach(rowType => {
                if (capturedData[rowType]) {
                    Object.keys(capturedData[rowType])
                        .filter(key => key.endsWith('Token'))
                        .forEach(key => {
                            const token = capturedData[rowType][key];
                            if (token) allRequiredTokens.add(token);
                        });
                }
            });
        });

        // Convert to array and subscribe via socket
        if (allRequiredTokens.size > 0 && socket.current) {
            const tokenArray = Array.from(allRequiredTokens);
            LogManager.log('Token', 'EnsuringSubscription', `Ensuring subscription for ${tokenArray.length} tokens for active moves.`);
            socket.current.emit('subscribe_3t', tokenArray);
        }
    };

    // Function to preserve tokens with the backend
    const preserveTokensWithBackend = () => {
        // Skip if no movements
        if (Object.keys(movingRows).length === 0 && Object.keys(activeMoving).length === 0) return;

        // Collect all tokens from all movements
        const allTokens = new Set();

        Object.keys({ ...movingRows, ...activeMoving }).forEach(rowKey => {
            const capturedData = capturedDistances[rowKey];
            if (!capturedData) return;

            ['sourceRow', 'targetRow'].forEach(rowType => {
                if (capturedData[rowType]) {
                    if (capturedData[rowType].s1Token) allTokens.add(capturedData[rowType].s1Token);
                    if (capturedData[rowType].s2Token) allTokens.add(capturedData[rowType].s2Token);
                    if (capturedData[rowType].s3Token) allTokens.add(capturedData[rowType].s3Token);
                    if (capturedData[rowType].s4Token) allTokens.add(capturedData[rowType].s4Token);
                }
            });
        });

        // Convert to array and call API
        const tokenArray = Array.from(allTokens);
        if (tokenArray.length > 0) {
            LogManager.log('Token', 'PreservingWithBackend', `Preserving ${tokenArray.length} tokens with backend`);

            axios.post(`${baseUrl}/preserve_tokens`, {
                tokens: tokenArray,
                sid: socket.current?.id
            })
                .then(response => {
                    LogManager.log('Token', 'PreservationResponse', response.data);
                })
                .catch(error => {
                    LogManager.log('Token', 'PreservationError', error.message || 'Unknown error');
                });
        }
    };

    // Function to reconcile tokens for move operations
    const reconcileMovementTokens = () => {
        // Skip if no movements
        if (Object.keys(movingRows).length === 0 && Object.keys(activeMoving).length === 0) return;

        let needsUpdate = false;

        // Get all current tokens from the UI tables
        const allCurrentTokens = new Set();
        [...peSideData, ...ceSideData].forEach(row => {
            if (row['S1-Token']) allCurrentTokens.add(row['S1-Token']);
            if (row['S2-Token']) allCurrentTokens.add(row['S2-Token']);
            if (row['S3-Token']) allCurrentTokens.add(row['S3-Token']);
            if (row['S4-Token']) allCurrentTokens.add(row['S4-Token']);
        });

        // For each active movement, check if all its tokens are still in the current UI
        Object.keys({ ...movingRows, ...activeMoving }).forEach(rowKey => {
            const capturedData = capturedDistances[rowKey];
            if (!capturedData || !capturedData.sourceRow || !capturedData.targetRow) return;

            const allMoveTokens = [
                ...Object.values(capturedData.sourceRow).filter(v => typeof v === 'number'),
                ...Object.values(capturedData.targetRow).filter(v => typeof v === 'number')
            ];

            const missingTokens = allMoveTokens.filter(token => token && !allCurrentTokens.has(token));

            if (missingTokens.length > 0) {
                LogManager.log('Token', 'ReconciliationNeeded', {
                    rowKey,
                    missing_tokens: missingTokens
                });
                needsUpdate = true;
            }
        });

        // If we found missing tokens, ensure subscription
        if (needsUpdate) {
            ensureMoveTokensSubscribed();
        }
    };

    // Modified function to handle M button click
    const handleMoveAction = (rowKey) => {
        if (rowActions[rowKey]) {
            // If + or - is already selected, don't allow M selection
            return;
        }

        setMovingRows((prev) => {
            const isCurrentlyActive = prev[rowKey];

            // If turning off, clear all related data for this move
            if (isCurrentlyActive) {
                const capturedData = capturedDistances[rowKey];
                LogManager.log('Trading Move', 'Position Move Deactivated', {
                    move_identifier: rowKey,
                    from_strike: capturedData?.sourceRow?.s1Strike || 'Unknown',
                });

                // Use a functional update to clear all states related to this rowKey
                setPairedMoves(p => { const n = { ...p }; Object.keys(n).forEach(k => { if (n[k] === rowKey || k === rowKey) delete n[k]; }); return n; });
                setMoveIndexValues(v => { const n = { ...v }; delete n[rowKey]; return v; });
                setMovePnlValues(v => { const n = { ...v }; delete n[rowKey]; return v; });
                setSelectedLotSizesForMove(s => { const n = { ...s }; delete n[rowKey]; return s; });
                setActiveMoving(a => { const n = { ...a }; delete n[rowKey]; return a; });
                setCapturedDistances(c => { const n = { ...c }; delete n[rowKey]; return c; });
                setDistanceChangedDuringMove(d => { const n = { ...d }; delete n[rowKey]; return d; });
                setMoveOperationStatus(s => { const n = { ...s }; delete n[rowKey]; return s; });
                setPreservedPnlValues(pnl => { const n = { ...pnl }; delete n[rowKey]; return n; });

                // Clear any running intervals for this move
                Object.keys(continuousMatchIntervals.current).forEach(key => {
                    if (key.startsWith(rowKey)) {
                        clearInterval(continuousMatchIntervals.current[key]);
                        delete continuousMatchIntervals.current[key];
                    }
                });

                const newMovingRows = { ...prev };
                delete newMovingRows[rowKey];
                return newMovingRows;
            }

            // Activating M for this row
            const side = rowKey.split('-')[0];
            const strikeVal = rowKey.split('-')[1];
            const sideData = side === 'PE' ? peSideData : ceSideData;
            const rowData = sideData.find(r => String(r['S1-Strike']) === strikeVal);

            if (!rowData) {
                toast.error("Row data not found");
                LogManager.log('Trading Move', 'Error', `Could not find data for ${rowKey}`);
                return prev;
            }

            updateMoveStatus(rowKey, 'info', 'Move ready');

            // Check for pairing
            const pairInfo = canPairMRows(rowKey);
            if (pairInfo) {
                setPairedMoves(prevPairs => ({ ...prevPairs, [rowKey]: pairInfo.key, [pairInfo.key]: rowKey }));
                toast.info(`Row ${rowKey} paired with ${pairInfo.key}.`);
                LogManager.log('Trading Move', 'Paired Position Activated', { move_identifier: rowKey, paired_with: pairInfo.key });
            }

            const currentDistance = side === 'PE' ? Math.round(data?.PE_SIDE?.Required_Distance) || 0 : Math.round(data?.CE_SIDE?.Required_Distance) || 0;
            const requiredDistance = side === 'PE' ? Math.round(data?.PE_SIDE?.Default_Required_Distance) || 0 : Math.round(data?.CE_SIDE?.Default_Required_Distance) || 0;
            const currentPnL = calculateCurrentPnL(rowData, side, ltpData);

            const capturedData = {
                activatedAt: Date.now(),
                sourceRow: {
                    s1Strike: rowData['S1-Strike'], s2Strike: rowData['S2-Strike'], s3Strike: rowData['S3-Strike'],
                    s1Token: rowData['S1-Token'], s2Token: rowData['S2-Token'], s3Token: rowData['S3-Token'],
                    s1Symbol: rowData['S1-Symbol'], s2Symbol: rowData['S2-Symbol'], s3Symbol: rowData['S3-Symbol'],
                    ...(rowData['S4-Strike'] && { s4Strike: rowData['S4-Strike'], s4Token: rowData['S4-Token'], s4Symbol: rowData['S4-Symbol'] })
                },
                modeState: {
                    useS4: (side === 'PE' ? peS4 : ceS4),
                    s3Multiplier: (side === 'PE' ? peS3Multiplier : ceS3Multiplier)
                },
                indexInfo: {
                    sourceRowIndex: sideData.findIndex(r => String(r['S1-Strike']) === strikeVal)
                }
            };

            LogManager.log('Trading Move', 'Position Move Activated', {
                move_identifier: rowKey,
                from_strike: rowData['S1-Strike'],
                initial_pnl: currentPnL.toFixed(2),
                mode: capturedData.modeState.useS4 ? '4T' : '3T',
                multiplier: capturedData.modeState.s3Multiplier,
            });

            setCapturedDistances(prev => ({ ...prev, [rowKey]: capturedData }));
            setMoveIndexValues(prev => ({ ...prev, [rowKey]: 2 }));
            setMovePnlValues(prev => ({ ...prev, [rowKey]: 0.05 }));

            return { ...prev, [rowKey]: true };
        });
    };

    // const handleToggleMoving = async (rowKey, side, row) => {
    //     const isActive = activeMoving[rowKey];

    //     if (isActive) {
    //         // Stop the move
    //         setActiveMoving(prev => { const next = { ...prev }; delete next[rowKey]; return next; });
    //         setExecutedQuantities(prev => { const next = { ...prev }; delete next[rowKey]; return next; });
    //         updateMoveStatus(rowKey, 'info', 'Move stopped by user.');
    //         LogManager.log('Trading Move', 'Position Move Stopped', { move_identifier: rowKey });

    //         // Clear running interval for this move
    //         Object.keys(continuousMatchIntervals.current).forEach(key => {
    //             if (key.startsWith(rowKey)) {
    //                 clearInterval(continuousMatchIntervals.current[key]);
    //                 delete continuousMatchIntervals.current[key];
    //             }
    //         });
    //         return;
    //     }

    //     if (!moveIndexValues[rowKey] || movePnlValues[rowKey] === undefined || !selectedLotSizesForMove[rowKey]) {
    //         toast.error("Please fill move index, target PnL, and select a lot size.");
    //         return;
    //     }

    //     const pairedRowKey = pairedMoves[rowKey];
    //     const sideData = side === 'PE' ? peSideData : ceSideData;
    //     const maxQty = maxOrderQuantities[instrument] || 1800;

    //     try {
    //          // Define buildTarget helper inside this scope
    //         const buildTarget = (cap, moveIdx, dataSide) => {
    //             if (cap.targetRow) return cap.targetRow; // Already built
    //             const srcIdx = cap.indexInfo.sourceRowIndex;
    //             const tgtIdx = srcIdx - moveIdx;
    //             if (tgtIdx < 0 || tgtIdx >= dataSide.length) {
    //                 throw new Error(`Invalid target row index (${tgtIdx}) for strike ${cap.sourceRow.s1Strike}`);
    //             }
    //             const tRow = dataSide[tgtIdx];
    //             return {
    //                 s1Strike: tRow['S1-Strike'], s2Strike: tRow['S2-Strike'], s3Strike: tRow['S3-Strike'],
    //                 s1Token: tRow['S1-Token'], s2Token: tRow['S2-Token'], s3Token: tRow['S3-Token'],
    //                 s1Symbol: tRow['S1-Symbol'], s2Symbol: tRow['S2-Symbol'], s3Symbol: tRow['S3-Symbol'],
    //                 ...(tRow['S4-Strike'] && { s4Strike: tRow['S4-Strike'], s4Token: tRow['S4-Token'], s4Symbol: tRow['S4-Symbol'] })
    //             };
    //         };

    //         let finalOrders = [];
    //         let isPairedMove = false;

    //         // PAIRED MOVE LOGIC
    //         if (pairedRowKey) {
    //              isPairedMove = true;
    //             if (!moveIndexValues[pairedRowKey] || movePnlValues[pairedRowKey] === undefined || !selectedLotSizesForMove[pairedRowKey]) {
    //                 toast.error(`Please complete all fields for the paired row ${pairedRowKey}`);
    //                 return;
    //             }

    //             const cap1 = capturedDistances[rowKey];
    //             const cap2 = capturedDistances[pairedRowKey];
    //             if (!cap1 || !cap2) throw new Error("Missing captured data for paired move.");

    //             const targetRow1 = buildTarget(cap1, parseInt(moveIndexValues[rowKey], 10), sideData);
    //             const targetRow2 = buildTarget(cap2, parseInt(moveIndexValues[pairedRowKey], 10), sideData);

    //             setCapturedDistances(prev => ({...prev, [rowKey]: {...prev[rowKey], targetRow: targetRow1}, [pairedRowKey]: {...prev[pairedRowKey], targetRow: targetRow2}}));

    //             const lot1 = selectedLotSizesForMove[rowKey];
    //             const lot2 = selectedLotSizesForMove[pairedRowKey];

    //             const createOrders = (seq, lot, rData, mode) => seq.map(o => ({...o, quantity: (o.tradingsymbol === rData.s3Symbol ? lot * (mode?.s3Multiplier || 1) : lot)}));

    //             let closeOrders1 = createOrderSequenceFromPreserved(cap1.sourceRow, '-', side).map(o => ({...o, quantity: (o.tradingsymbol === cap1.sourceRow.s3Symbol ? lot1 * (cap1.modeState?.s3Multiplier || 1) : lot1), exchangeType: "NFO", selectedindex: instrument}));
    //             let placeOrders1 = createOrderSequenceFromPreserved(targetRow1, '+', side).map(o => ({...o, quantity: (o.tradingsymbol === targetRow1.s3Symbol ? lot1 * (cap1.modeState?.s3Multiplier || 1) : lot1), exchangeType: "NFO", selectedindex: instrument}));
    //             let closeOrders2 = createOrderSequenceFromPreserved(cap2.sourceRow, '-', side).map(o => ({...o, quantity: (o.tradingsymbol === cap2.sourceRow.s3Symbol ? lot2 * (cap2.modeState?.s3Multiplier || 1) : lot2), exchangeType: "NFO", selectedindex: instrument}));
    //             let placeOrders2 = createOrderSequenceFromPreserved(targetRow2, '+', side).map(o => ({...o, quantity: (o.tradingsymbol === targetRow2.s3Symbol ? lot2 * (cap2.modeState?.s3Multiplier || 1) : lot2), exchangeType: "NFO", selectedindex: instrument}));

    //             finalOrders = [...closeOrders1, ...placeOrders1, ...closeOrders2, ...placeOrders2];

    //         } else { // SINGLE MOVE LOGIC
    //             const cap = capturedDistances[rowKey];
    //             if (!cap) throw new Error("Missing captured data for single move.");

    //             const targetRow = buildTarget(cap, parseInt(moveIndexValues[rowKey], 10), sideData);
    //             setCapturedDistances(prev => ({...prev, [rowKey]: {...prev[rowKey], targetRow}}));

    //             const lot = selectedLotSizesForMove[rowKey];
    //             let closeOrders = createOrderSequenceFromPreserved(cap.sourceRow, '-', side).map(o => ({...o, quantity: (o.tradingsymbol === cap.sourceRow.s3Symbol ? lot * (cap.modeState?.s3Multiplier || 1) : lot), exchangeType: "NFO", selectedindex: instrument}));
    //             let placeOrders = createOrderSequenceFromPreserved(targetRow, '+', side).map(o => ({...o, quantity: (o.tradingsymbol === targetRow.s3Symbol ? lot * (cap.modeState?.s3Multiplier || 1) : lot), exchangeType: "NFO", selectedindex: instrument}));

    //             finalOrders = [...closeOrders, ...placeOrders];
    //         }

    //          // MARGIN CHECK (PRESERVED)
    //         if (checkMargin) {
    //             const marginPayload = prepareMarginRequest(finalOrders);
    //             LogManager.log("Move", "MarginCheckStart", { type: isPairedMove ? "paired" : "single", rowKey });
    //             try {
    //                 await axios.post(`${baseUrl}/margin`, marginPayload);
    //                 LogManager.log("Move", "MarginCheckPassed", { type: isPairedMove ? "paired" : "single" });
    //             } catch (err) {
    //                 const errorData = err.response?.data || {};
    //                 const { error: msg, required_margin, available_margin, difference } = errorData;
    //                 const logPayload = { type: isPairedMove ? "paired" : "single", serverError: msg, required_margin, available_margin, difference };
    //                 LogManager.log("Move", "MarginCheckFailed", logPayload);
    //                 toast.error(`Margin Error: ${msg || err.message}`);
    //                 return; // Stop execution if margin check fails
    //             }
    //         }


    //         // ACTIVATE THE MOVE
    //         if (isPairedMove) {
    //             const lot1 = selectedLotSizesForMove[rowKey];
    //             const lot2 = selectedLotSizesForMove[pairedRowKey];
    //             const batches1 = Math.ceil(lot1 / maxQty);
    //             const batches2 = Math.ceil(lot2 / maxQty);
    //             setActiveMoving(prev => ({ ...prev, [rowKey]: true, [pairedRowKey]: true }));
    //             setExecutedQuantities(prev => ({ ...prev, [rowKey]: { executed:0, total:lot1, pending:lot1, batches:batches1, processedBatches:0 }, [pairedRowKey]: { executed:0, total:lot2, pending:lot2, batches:batches2, processedBatches:0 }}));
    //             updateMoveStatus(rowKey, 'info', 'Monitoring paired PnL');
    //             updateMoveStatus(pairedRowKey, 'info', 'Monitoring paired PnL');
    //             LogManager.log('Trading Move', 'Paired Position Move Started', { pair_identifier: `${rowKey}-${pairedRowKey}` });
    //         } else {
    //             const lot = selectedLotSizesForMove[rowKey];
    //             const batches = Math.ceil(lot / maxQty);
    //             setActiveMoving(prev => ({ ...prev, [rowKey]: true }));
    //             setExecutedQuantities(prev => ({ ...prev, [rowKey]: { executed:0, total:lot, pending:lot, batches, processedBatches:0 }}));
    //             updateMoveStatus(rowKey, 'info', 'Monitoring PnL');
    //             LogManager.log('Trading Move', 'Position Move Started', { move_identifier: rowKey });
    //         }
    //         ensureMoveTokensSubscribed();

    //     } catch (err) {
    //         LogManager.log('Move', 'UnexpectedError', { rowKey, error: err.message });
    //         toast.error(err.message || 'Unexpected error during move setup');
    //     }
    // };

    // Handle move-related input changes


    const handleToggleMoving = async (rowKey, side) => {
        // --- Stop Logic ---
        if (activeMoving[rowKey]) {
            setActiveMoving(p => { const n = { ...p }; delete n[rowKey]; if (p[rowKey]) delete n[p[rowKey]]; return n; });
            LogManager.log('Trading Move', 'Position Move Stopped', { move_identifier: rowKey });
            Object.keys(continuousMatchIntervals.current).forEach(k => { if (k.startsWith(rowKey)) { clearInterval(continuousMatchIntervals.current[k]); delete continuousMatchIntervals.current[k]; } });
            return;
        }

        const isPaired = !!pairedMoves[rowKey];
        const key2 = isPaired ? pairedMoves[rowKey] : null;

        // --- Input Validation ---
        if (!moveIndexValues[rowKey] || movePnlValues[rowKey] === undefined || !selectedLotSizesForMove[rowKey] || (isPaired && (!moveIndexValues[key2] || movePnlValues[key2] === undefined || !selectedLotSizesForMove[key2]))) {
            toast.error("Please fill all move fields for selected row(s)."); return;
        }

        const sideData = side === 'PE' ? peSideData : ceSideData;
        let allMarginOrders = [];
        let moveLogIdentifier = `single-${rowKey}`;

        try {
            // --- Helper to build target row ---
            const buildTarget = (capData, moveIdx) => {
                const tgtIdx = capData.indexInfo.sourceRowIndex - parseInt(moveIdx, 10);
                if (tgtIdx < 0 || tgtIdx >= sideData.length) throw new Error(`Invalid target index for ${capData.sourceRow.s1Strike}.`);
                const targetRowData = sideData[tgtIdx];
                return {
                    s1Strike: targetRowData['S1-Strike'], s2Strike: targetRowData['S2-Strike'], s3Strike: targetRowData['S3-Strike'],
                    s1Token: targetRowData['S1-Token'], s2Token: targetRowData['S2-Token'], s3Token: targetRowData['S3-Token'],
                    s1Symbol: targetRowData['S1-Symbol'], s2Symbol: targetRowData['S2-Symbol'], s3Symbol: targetRowData['S3-Symbol'],
                    ...(targetRowData['S4-Strike'] && { s4Strike: targetRowData['S4-Strike'], s4Token: targetRowData['S4-Token'], s4Symbol: targetRowData['S4-Symbol'] })
                };
            };

            // --- Build Orders for Margin Check ---
            const createOrdersForMargin = (rKey, capData) => {
                const lot = selectedLotSizesForMove[rKey];
                const moveIdx = moveIndexValues[rKey];
                const targetRow = buildTarget(capData, moveIdx);
                setCapturedDistances(p => ({ ...p, [rKey]: { ...p[rKey], targetRow } }));
                const create = (seq, baseQty, row, mode) => seq.map(o => ({ ...o, quantity: (o.tradingsymbol === row.s3Symbol ? baseQty * (mode?.s3Multiplier || 1) : baseQty) }));
                const closeOrders = create(createOrderSequenceFromPreserved(capData.sourceRow, '-', side), lot, capData.sourceRow, capData.modeState);
                const placeOrders = create(createOrderSequenceFromPreserved(targetRow, '+', side), lot, targetRow, capData.modeState);
                return [...closeOrders, ...placeOrders];
            };

            allMarginOrders.push(...createOrdersForMargin(rowKey, capturedDistances[rowKey]));
            if (isPaired) {
                allMarginOrders.push(...createOrdersForMargin(key2, capturedDistances[key2]));
                moveLogIdentifier = `paired-${rowKey}-${key2}`;
            }

            /*******************************************************************
             * START OF MARGIN CHECK BLOCK
             *******************************************************************/
            if (grpCheckMargin) {
                const marginPayload = {
                    orders: allMarginOrders.map(o => ({
                        tradingsymbol: o.tradingsymbol,
                        direction: o.transaction_type.toUpperCase(),
                        quantity: o.quantity,
                        exchangeType: instrument === "CRUDEOIL" ? "MCX" : (instrument === "SENSEX" || instrument === "BANKEX") ? "BFO" : "NFO",
                        selectedindex: instrument
                    })),
                    move_position: true
                };

                LogManager.log("Move", "MarginCheckStart", { move: moveLogIdentifier, payload: marginPayload });
                try {
                    await axios.post(`${baseUrl}/margin`, marginPayload);
                    LogManager.log("Move", "MarginCheckPassed", { move: moveLogIdentifier });
                } catch (err) {
                    const errData = err.response?.data || {};
                    const { error, required_margin, available_margin, difference } = errData;
                    LogManager.log("Move", "MarginCheckFailed", { move: moveLogIdentifier, error, required_margin, available_margin, difference });
                    toast.error(`Margin Error: ${error || err.message}\nRequired: ${required_margin}, Available: ${available_margin}`);
                    return; // CRITICAL: Halt execution if margin check fails
                }
            }
            /*******************************************************************
             * END OF MARGIN CHECK BLOCK
             *******************************************************************/

            // --- Activate Move ---
            const activateMove = (rKey) => {
                const lot = selectedLotSizesForMove[rKey];
                const batches = Math.ceil(lot / (maxOrderQuantities[instrument] || 1800));
                setExecutedQuantities(p => ({ ...p, [rKey]: { executed: 0, total: lot, pending: lot, batches, processedBatches: 0 } }));
            }
            setActiveMoving(p => ({ ...p, [rowKey]: true, ...(isPaired && { [key2]: true }) }));
            activateMove(rowKey);
            if (isPaired) activateMove(key2);
            LogManager.log('Trading Move', 'Position Move Started', { move: moveLogIdentifier });

        } catch (err) {
            toast.error(`Move Start Failed: ${err.message}`);
            LogManager.log('Move', 'StartError', { move: moveLogIdentifier, error: err.message });
        }
    };

    const handleMoveIndexChange = (rowKey, value) => {
        setMoveIndexValues((prev) => ({
            ...prev,
            [rowKey]: value
        }));

        // Dynamically update target row data for immediate feedback (optional, but good UX)
        const capturedData = capturedDistances[rowKey];
        const side = rowKey.split('-')[0];
        const sideData = side === 'PE' ? peSideData : ceSideData;
        const sourceRowIndex = capturedData?.indexInfo?.sourceRowIndex;

        if (sourceRowIndex !== undefined) {
            const moveIndex = parseInt(value, 10);
            if (!isNaN(moveIndex)) {
                const targetRowIndex = sourceRowIndex - moveIndex;
                if (targetRowIndex >= 0 && targetRowIndex < sideData.length) {
                    const targetRow = sideData[targetRowIndex];
                    setCapturedDistances(prev => ({
                        ...prev,
                        [rowKey]: {
                            ...prev[rowKey],
                            targetRow: { // Store the full target row data
                                s1Strike: targetRow['S1-Strike'], s2Strike: targetRow['S2-Strike'], s3Strike: targetRow['S3-Strike'],
                                s1Token: targetRow['S1-Token'], s2Token: targetRow['S2-Token'], s3Token: targetRow['S3-Token'],
                                s1Symbol: targetRow['S1-Symbol'], s2Symbol: targetRow['S2-Symbol'], s3Symbol: targetRow['S3-Symbol'],
                                ...(targetRow['S4-Strike'] && { s4Strike: targetRow['S4-Strike'], s4Token: targetRow['S4-Token'], s4Symbol: targetRow['S4-Symbol'] })
                            }
                        }
                    }));
                }
            }
        }
    };


    const handleMovePnlChange = (rowKey, value) => {
        setMovePnlValues((prev) => ({
            ...prev,
            [rowKey]: value
        }));
    };

    const handleLotSizeForMoveSelection = (rowKey, size) => {
        setSelectedLotSizesForMove((prev) => ({
            ...prev,
            [rowKey]: size
        }));
        LogManager.log('Trading Move', 'Quantity Selected', {
            move_identifier: rowKey,
            selected_quantity: size
        });
    };

    // Function to get M button style
    const getMButtonStyle = (rowKey) => {
        let s = {
            margin: '4px',
            fontSize: '14px',
            padding: '4px 8px',
            color: '#ffffff',
            minWidth: '40px'
        };

        if (activeMoving[rowKey]) {
            s.backgroundColor = 'green'; // Green when actively moving
        } else if (movingRows[rowKey]) {
            s.backgroundColor = '#057fc3'; // Blue when selected
        } else {
            s.backgroundColor = 'grey';
        }

        return s;
    };

    // Function to get lot size button style for move
    const getLotSizeForMoveButtonStyle = (rowKey, size) => {
        let s = {
            margin: '4px',
            fontSize: '14px',
            padding: '4px 8px',
            color: '#ffffff',
            minWidth: '50px'
        };

        if (selectedLotSizesForMove[rowKey] === size) {
            s.backgroundColor = '#8A2BE2'; // Purple when selected for move
        } else {
            s.backgroundColor = 'grey';
        }

        return s;
    };

    // Render execution status for move operations
    const renderExecutionStatus = (rowKey) => {
        const executionData = executedQuantities[rowKey];
        if (!executionData) return null;

        const { executed, total, batches, processedBatches } = executionData;
        if (executed < total && total > 0) {
            return (
                <span style={{ marginLeft: '4px', fontSize: '11px', color: '#ffaa00' }}>
                    {executed}/{total} {batches > 1 ? `(${(processedBatches || 0)}/${batches})` : ''}
                </span>
            );
        }
        return null;
    };

    // Render paired move indicator
    const renderPairedMoveIndicator = (rowKey) => {
        const isPaired = pairedMoves[rowKey];
        if (!isPaired) return null;

        return (
            <span style={{
                color: '#FF00FF',
                fontSize: '10px',
                margin: '0 2px',
                whiteSpace: 'nowrap'
            }}>
                Paired:{isPaired.split('-')[1]}
            </span>
        );
    };

    // Function to render move status indicator
    const renderMoveStatusIndicator = (rowKey) => {
        const status = moveOperationStatus[rowKey];

        // Only show status for distance changes - hide all other statuses
        if (!status || !distanceChangedDuringMove[rowKey]) return null;

        return (
            <div
                style={{
                    fontSize: '10px',
                    color: 'orange',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                    marginLeft: '4px'
                }}
            >
                Distance changed
            </div>
        );
    };

    // Modified table data cell for Place Order column - with improved UI
    const renderPlaceOrderCell = (row, side, rowKey, selectedAction) => {
        const isMoving = movingRows[rowKey];
        const isActive = activeMoving[rowKey];

        return (
            <td className="lot-size-buttons">
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Regular trading buttons */}
                    <div style={{ display: 'flex', marginRight: '8px' }}>
                        <Button
                            onClick={() => handleRowAction(rowKey, '+')}
                            style={getButtonStyle('+', selectedAction)}
                            disabled={isFreezed || isMoving}
                        >
                            +
                        </Button>
                        <Button
                            onClick={() => handleRowAction(rowKey, '-')}
                            style={getButtonStyle('-', selectedAction)}
                            disabled={isFreezed || isMoving}
                        >
                            -
                        </Button>

                        {/* M button */}
                        <Button
                            onClick={() => handleMoveAction(rowKey)}
                            style={getMButtonStyle(rowKey)}
                            disabled={isFreezed || selectedAction}
                            className={activeMoving[rowKey] ? 'blink' : ''}
                        >
                            M
                        </Button>
                    </div>

                    {/* Show move controls inline when M is active */}
                    {isMoving ? (
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            padding: '2px 4px',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '4px',
                            marginRight: '4px'
                        }}>

                            {/* Input fields */}
                            <input
                                type="number"
                                placeholder="Idx"
                                value={moveIndexValues[rowKey] || ''}
                                onChange={(e) => handleMoveIndexChange(rowKey, e.target.value)}
                                style={{ width: '35px', margin: '0 2px' }}
                                disabled={isActive}
                            />
                            <input
                                type="number"
                                placeholder="PnL"
                                step="0.05"
                                value={movePnlValues[rowKey] !== undefined ? movePnlValues[rowKey] : 0.05}
                                onChange={(e) => handleMovePnlChange(rowKey, e.target.value)}
                                style={{ width: '45px', margin: '0 2px' }}
                                disabled={isActive}
                            />

                            {/* Lot Size Buttons for Move */}
                            {lotSizes.slice(0, 3).sort((a, b) => a - b).map((size, idx2) => (
                                <Button
                                    key={`${side.toLowerCase()}-move-${rowKey}-${idx2}`}
                                    onClick={() => handleLotSizeForMoveSelection(rowKey, size)}
                                    style={getLotSizeForMoveButtonStyle(rowKey, size)}
                                    disabled={isFreezed || isActive}
                                >
                                    {size}
                                </Button>
                            ))}

                            <Button
                                onClick={() => handleToggleMoving(rowKey, side, row)}
                                style={{
                                    backgroundColor: isActive ? 'red' : 'green',
                                    color: 'white',
                                    padding: '2px 6px',
                                    fontSize: '12px',
                                    margin: '0 4px'
                                }}
                                className={isActive ? 'blink' : ''}
                            >
                                {isActive ? 'Stop' : 'Go'}
                                {renderExecutionStatus(rowKey)}
                            </Button>

                            {renderPairedMoveIndicator(rowKey)}
                            {renderMoveStatusIndicator(rowKey)}
                        </div>
                    ) : (
                        // Show regular lot size buttons when M is not active
                        <div>
                            {lotSizes.sort((a, b) => a - b).map((size, idx2) => (
                                <Button
                                    key={`${side.toLowerCase()}-${rowKey}-${idx2}`}
                                    onClick={() => handlePlaceOrder(row, size, selectedAction, side, rowKey)}
                                    style={getQuantityButtonStyle(selectedAction)}
                                    disabled={isFreezed || !selectedAction}
                                >
                                    {size}
                                </Button>
                            ))}
                        </div>
                    )}
                </div>
            </td>
        );
    };


    // -------------------- UI HANDLERS --------------------
    const openSettingsModal = () => {
        setModalInstrument(instrument || 'NIFTY');
        setModalExpiryDate(expiryDates[instrument]?.[0] || null);
        setModalLotSizes(lotSizes.length > 0 ? lotSizes : lotSizeOptions[instrument]?.slice(0, 3) || []);
        setModalCustomLotSizes(['', '', '']);
        setModalNumRows(numRows);
        setModalNumItms(itms);
        setShowSettings(true);
    };

    const handleUpdateSettings = () => {
        setShowSettings(false);
        setInstrument(modalInstrument);
        setExpiryDate(modalExpiryDate);
        setLotSizes(modalLotSizes);
        setNumRows(modalNumRows);
        setNumItms(modalNumItms);
        setCeSelectedStrikeInterval(null);
        setPeSelectedStrikeInterval(null);
        // Clear ALL stale data from previous selection
        setCeSideData([]);
        setPeSideData([]);
        setLockedCeSideData([]);
        setLockedPeSideData([]);
        setRowActions({});
        setPeSelectedRows([]);
        setCeSelectedRows([]);
        setPeHas3rdCheckboxError(false);
        setCeHas3rdCheckboxError(false);
        setRawLtpData({});
        setLtpData({});
        setData({});
        setCePeValuesOptions([]);
        setPeS3Multiplier(1);
        setCeS3Multiplier(1);
        setPeS4(false);
        setCeS4(false);

        LogManager.log('Settings', 'Updated', {
            instrument: modalInstrument,
            expiry: modalExpiryDate?.toISOString().split("T")[0],
            lotSizes: modalLotSizes,
            numRows: modalNumRows,
            numItms: modalNumItms
        });

        setTimeout(() => {
            fetchData();
        }, 0);
    };

    const handleCustomLotSizeChange = (index, value) => {
        const updated = [...modalCustomLotSizes];
        updated[index] = value;
        setModalCustomLotSizes(updated);
        setModalLotSizes((prev) => {
            const filtered = prev.filter((sz) => !modalCustomLotSizes.includes(sz));
            const valid = updated.filter((sz) => sz > 0);
            return [...filtered, ...valid];
        });
    };

    // Function to toggle log viewer
    const toggleLogViewer = () => {
        if (!showLogs) {
            // When opening logs, fetch recent log entries
            fetchRecentLogs();
        }
        setShowLogs(!showLogs);
    };

    // Function to fetch recent logs
    const fetchRecentLogs = async () => {
        try {
            const response = await axios.get(`${baseUrl}/get_logs`);
            if (response.data && response.data.entries) {
                setLogEntries(response.data.entries);
            }
        } catch (error) {
            toast.error("Failed to fetch logs");
            console.error("Error fetching logs:", error);
        }
    };

    // Function to filter logs
    const filterLogs = (entry) => {
        if (!logFilter) return true;

        const filterLower = logFilter.toLowerCase();
        return (
            (entry.component && entry.component.toLowerCase().includes(filterLower)) ||
            (entry.action && entry.action.toLowerCase().includes(filterLower)) ||
            (entry.details && entry.details.toLowerCase().includes(filterLower))
        );
    };

    // Interval buttons
    const handleStrikeIntervalClick = (side, value) => {
        freezeUI();
        if (side === 'CE') {
            oldCeS3DataRef.current = (isStrikeLockActive ? lockedCeSideData : ceSideData).map((item) => ({
                s3Strike: item['S3-Strike'],
                s3Token: item['S3-Token'],
            }));
            setCeSelectedStrikeInterval(value);
        } else {
            oldPeS3DataRef.current = (isStrikeLockActive ? lockedPeSideData : peSideData).map((item) => ({
                s3Strike: item['S3-Strike'],
                s3Token: item['S3-Token'],
            }));
            setPeSelectedStrikeInterval(value);
        }
        setIsUpdatingS3(true);
        fetchData();

        LogManager.log('Strike', 'IntervalChanged', {
            side,
            value
        });
    };

    // Color logic
    const renderStrikeIntervalButtons = (side, requiredDistance, defaultRequiredDistance) => {
        const interval = instrumentStrikeIntervals[instrument];
        const currentStrike = defaultRequiredDistance || 0;
        const N = 12;
        const startStrike = Math.max(currentStrike - N * interval, interval);
        const endStrike = currentStrike + N * interval;
        let strikes = [];
        for (let s = startStrike; s <= endStrike; s += interval) {
            strikes.push(s);
        }
        strikes = strikes.filter((x) => x !== 0);

        const selectedVal = (side === 'CE') ? ceSelectedStrikeInterval : peSelectedStrikeInterval;

        return (
            <div className="strike-buttons">
                {strikes.map((st) => {
                    const isSelected = (selectedVal === st);
                    let btnStyle = {
                        minWidth: '50px',
                        minHeight: '30px',
                        fontSize: '14px',
                        margin: '2px',
                        color: '#ffffff'
                    };
                    if (st === Math.round(requiredDistance)) {
                        btnStyle.backgroundColor = '#057fc3'; // blue
                        if (isSelected) {
                            btnStyle.backgroundImage = 'linear-gradient(45deg, blue 50%, green 50%)';
                        }
                    } else if (isSelected) {
                        btnStyle.backgroundColor = 'green';
                    } else {
                        btnStyle.backgroundColor = '#ff6347'; // tomato
                    }

                    return (
                        <Button
                            key={st}
                            variant="none"
                            style={btnStyle}
                            onClick={() => handleStrikeIntervalClick(side, st)}
                            disabled={isFreezed}
                        >
                            {st}
                        </Button>
                    );
                })}
            </div>
        );
    };

    // S3 MULTIPLIERS – using consistent styling and disabling when T4 is selected.
    const renderS3MultiplierButtons = (side) => {
        const multipliers = [2, 3, 4, 5, 6];
        const currentMult = (side === 'PE') ? peS3Multiplier : ceS3Multiplier;
        const t4Selected = (side === 'PE') ? peS4 : (side === 'CE' ? ceS4 : false);

        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '6px',
                }}
            >
                {multipliers.map((m) => {
                    const isActive = (m === currentMult);
                    return (
                        <Button
                            key={m}
                            onClick={() => {
                                if (!t4Selected) {
                                    if (isActive) {
                                        if (side === 'PE') setPeS3Multiplier(1);
                                        else setCeS3Multiplier(1);
                                    } else {
                                        if (side === 'PE') setPeS3Multiplier(m);
                                        else setCeS3Multiplier(m);
                                    }

                                    LogManager.log('Multiplier', 'Changed', {
                                        side,
                                        multiplier: isActive ? 1 : m
                                    });
                                }
                            }}
                            disabled={isFreezed || t4Selected}
                            style={{
                                width: '30px',
                                height: '30px',
                                backgroundColor: isActive ? 'yellow' : 'grey',
                                color: 'black',
                                fontSize: '16px'
                            }}
                        >
                            {m}X
                        </Button>
                    );
                })}
            </div>
        );
    };

    // Row actions
    const handleRowAction = (rowKey, action) => {
        // If M is active for this row, don't allow + or - actions
        if (movingRows[rowKey]) {
            return;
        }

        setRowActions((prev) => {
            const curr = prev[rowKey];
            const newVal = (curr === action) ? null : action;

            LogManager.log('Action', 'RowAction', {
                rowKey,
                action,
                previous: curr,
                new: newVal
            });

            return { ...prev, [rowKey]: newVal };
        });
    };

    const getButtonStyle = (action, selectedAction) => {
        let s = {
            margin: '4px',
            fontSize: '14px',
            padding: '4px 8px',
            color: '#ffffff',
            minWidth: '40px'
        };
        if (selectedAction === action) {
            s.backgroundColor = (action === '+') ? 'green' : '#ff6347';
        } else {
            s.backgroundColor = 'grey';
        }
        return s;
    };

    const getQuantityButtonStyle = (selectedAction) => {
        let s = {
            margin: '4px',
            fontSize: '14px',
            padding: '4px 8px',
            color: '#ffffff',
            minWidth: '50px'
        };
        if (selectedAction === '+') {
            s.backgroundColor = 'green';
        } else if (selectedAction === '-') {
            s.backgroundColor = '#ff6347';
        } else {
            s.backgroundColor = 'grey';
        }
        return s;
    };

    const getStrikeIndicator = (row, side, atmValue) => {
        const s1Strike = row['S1-Strike'];
        if (!s1Strike || !atmValue) return { label: '', color: 'inherit', rowClass: '' };
        if (side === 'PE') {
            if (s1Strike === atmValue) {
                return { label: 'ATM', color: '#cce71d', rowClass: 'atm' };
            } else if (s1Strike > atmValue) {
                return { label: 'ITM', color: '#ff6347', rowClass: 'itm' };
            } else {
                return { label: 'OTM', color: '#0fff0f', rowClass: 'otm' };
            }
        } else {
            // CE
            if (s1Strike === atmValue) {
                return { label: 'ATM', color: '#cce71d', rowClass: 'atm' };
            } else if (s1Strike < atmValue) {
                return { label: 'ITM', color: '#ff6347', rowClass: 'itm' };
            } else {
                return { label: 'OTM', color: '#0fff0f', rowClass: 'otm' };
            }
        }
    };

    // Build orders
    const createOrderSequence = (row, selectedAction, side) => {
        const s1 = row['S1-Symbol'];
        const s2 = row['S2-Symbol'];
        const s3 = row['S3-Symbol'];
        const s4 = row['S4-Symbol']; // might be undefined

        let orderSequence = [];
        if (selectedAction === '+') {
            // BUY S1, SELL S2, S3, S4
            orderSequence.push({ tradingsymbol: s1, transaction_type: "BUY" });
            orderSequence.push({ tradingsymbol: s2, transaction_type: "SELL" });
            orderSequence.push({ tradingsymbol: s3, transaction_type: "SELL" });
            if (s4) {
                orderSequence.push({ tradingsymbol: s4, transaction_type: "SELL" });
            }
        } else {
            // EXIT => depends on exitMethod
            const exitMethod = (side === 'CE') ? ceExitMethod : peExitMethod;
            if (exitMethod === 'S2') {
                // S2->S3->S4 => BUY, then SELL S1
                if (s2) orderSequence.push({ tradingsymbol: s2, transaction_type: "BUY" });
                if (s3) orderSequence.push({ tradingsymbol: s3, transaction_type: "BUY" });
                if (s4) orderSequence.push({ tradingsymbol: s4, transaction_type: "BUY" });
                if (s1) orderSequence.push({ tradingsymbol: s1, transaction_type: "SELL" });
            } else {
                // S3->S4->S2 => BUY, then SELL S1
                if (s3) orderSequence.push({ tradingsymbol: s3, transaction_type: "BUY" });
                if (s4) orderSequence.push({ tradingsymbol: s4, transaction_type: "BUY" });
                if (s2) orderSequence.push({ tradingsymbol: s2, transaction_type: "BUY" });
                if (s1) orderSequence.push({ tradingsymbol: s1, transaction_type: "SELL" });
            }
        }
        return orderSequence;
    };

    const prepareMarginRequest = (combinedOrders) => {
        return {
            orders: combinedOrders,
            move_position: true,
        };
    };

    // Main placeOrder (continued)
    const handlePlaceOrder = async (row, lotSize, selectedAction, side, rowKey) => {
        if (!selectedAction || !lotSize) {
            toast.error("Please select an action (+ or -) and a lot size.");
            return;
        }
        setIsLoading(true);

        const isRowSelected = (side === 'PE') ? peSelectedRows.includes(rowKey) : ceSelectedRows.includes(rowKey);
        const sideSelectedRows = (side === 'PE') ? peSelectedRows : ceSelectedRows;

        let combinedOrders = [];
        const getExchangeType = (instr) => instr === "CRUDEOIL" ? "MCX" : (instr === "SENSEX" || instr === "BANKEX") ? "BFO" : "NFO";   // 2026-05-14: MCX route

        if (isRowSelected) {
            // Multi-order
            for (const rk of sideSelectedRows) {
                const action = rowActions[rk];
                if (!action) continue;
                const strikeVal = rk.split('-')[1];
                const foundRow = (side === 'PE' ? peSideData : ceSideData).find(
                    (rw) => String(rw['S1-Strike']) === strikeVal
                );
                if (!foundRow) continue;
                const seq = createOrderSequence(foundRow, action, side);
                seq.forEach((o) => {
                    const multiplier = (side === 'PE') ? peS3Multiplier : ceS3Multiplier;
                    // Only multiply S3, not S4
                    const isS3 = (o.tradingsymbol === foundRow['S3-Symbol']);
                    const realQty = isS3 ? lotSize * multiplier : lotSize;

                    combinedOrders.push({
                        tradingsymbol: o.tradingsymbol,
                        direction: o.transaction_type.toUpperCase(),
                        quantity: realQty,
                        exchangeType: getExchangeType(instrument),
                        selectedindex: instrument
                    });
                });
            }

            LogManager.log('Order', 'MultiOrderPrepared', {
                side,
                action: selectedAction,
                rowCount: sideSelectedRows.length,
                lotSize,
                orderCount: combinedOrders.length
            });
        } else {
            // Single row
            const strikeVal = rowKey.split('-')[1];
            const foundRow = (side === 'PE' ? peSideData : ceSideData).find(
                (rw) => String(rw['S1-Strike']) === strikeVal
            );
            if (!foundRow) {
                toast.error("Row data not found.");
                LogManager.log('Order', 'Error', `Row data not found for ${rowKey}`);
                setIsLoading(false);
                return;
            }
            const seq = createOrderSequence(foundRow, selectedAction, side);
            seq.forEach((o) => {
                const multiplier = (side === 'PE') ? peS3Multiplier : ceS3Multiplier;
                // Only multiply S3, not S4
                const isS3 = (o.tradingsymbol === foundRow['S3-Symbol']);
                const realQty = isS3 ? lotSize * multiplier : lotSize;

                combinedOrders.push({
                    tradingsymbol: o.tradingsymbol,
                    direction: o.transaction_type.toUpperCase(),
                    quantity: realQty,
                    exchangeType: getExchangeType(instrument),
                    selectedindex: instrument
                });
            });

            LogManager.log('Order', 'SingleOrderPrepared', {
                rowKey,
                side,
                action: selectedAction,
                lotSize,
                multiplier: (side === 'PE') ? peS3Multiplier : ceS3Multiplier,
                orderCount: combinedOrders.length
            });
        }

        // Margin check
        if (checkMargin) {
            const marginPayload = prepareMarginRequest(combinedOrders);
            LogManager.log('Order', 'MarginCheckStarted', {
                orderCount: combinedOrders.length
            });

            try {
                const marginResponse = await axios.post(`${baseUrl}/margin`, marginPayload);
                if (!(marginResponse.status >= 200 && marginResponse.status < 300)) {
                    toast.error("Unexpected margin check error.");
                    LogManager.log('Order', 'MarginCheckError', "Unexpected status code");
                    setIsLoading(false);
                    return;
                }

                LogManager.log('Order', 'MarginCheckPassed', marginResponse.data);
            } catch (err) {
                if (err.response && err.response.data) {
                    const { error: errorMsg, required_margin, available_margin, difference } = err.response.data;
                    toast.error(
                        `Error: ${errorMsg}\nRequired: ${required_margin}\nAvailable: ${available_margin}\nDifference: ${difference}`
                    );

                    LogManager.log('Order', 'MarginCheckFailed', {
                        error: errorMsg,
                        required: required_margin,
                        available: available_margin,
                        difference: difference
                    });
                } else {
                    toast.error("Failed margin check.");
                    LogManager.log('Order', 'MarginCheckFailed', "Unknown error");
                }
                setIsLoading(false);
                return;
            }
        } else {
            LogManager.log('Order', 'MarginCheckSkipped', "User disabled margin check");
        }

        // Place Orders
        try {
            const orderEndpoint = icebergEnabled ? '/iceberg_orders' : '/preprocess_orders';
            const payload = { orders: combinedOrders, move_position: false, market_protection: marketProtection || 30 };
            LogManager.log('Order', 'SendingOrderRequest', {
                orderCount: combinedOrders.length,
                iceberg: icebergEnabled,
                endpoint: orderEndpoint,
            });

            const response = await axios.post(`${baseUrl}${orderEndpoint}`, payload);
            toast.success("Orders processed successfully!");
            playOrderSuccessSound();  // 2026-05-11 (#3): kotak-only ding on success

            LogManager.log('Order', 'OrderSuccess', {
                response: response.data
            });
        } catch (error) {
            console.error(error);
            toast.error("Error processing orders.");

            LogManager.log('Order', 'OrderFailed', {
                error: error.message || "Unknown error"
            });
        }
        setIsLoading(false);
    };

    const handleRowCheckboxChange = (side, rowKey, checked) => {
        if (side === 'PE') {
            setPeSelectedRows((prev) => {
                if (checked) {
                    if (prev.length >= 4) {
                        if (!peHas3rdCheckboxError) {
                            toast.error("Cannot select more than 4 on PE side.");
                            setPeHas3rdCheckboxError(true);
                        }
                        return prev;
                    } else {
                        const newArr = [...prev, rowKey];
                        if (newArr.length <= 4) setPeHas3rdCheckboxError(false);

                        LogManager.log('Selection', 'RowSelected', {
                            side: 'PE',
                            rowKey,
                            totalSelected: newArr.length
                        });

                        return newArr;
                    }
                } else {
                    const newArr = prev.filter((x) => x !== rowKey);
                    if (newArr.length < 4) setPeHas3rdCheckboxError(false);

                    LogManager.log('Selection', 'RowDeselected', {
                        side: 'PE',
                        rowKey,
                        totalSelected: newArr.length
                    });

                    return newArr;
                }
            });
        } else {
            setCeSelectedRows((prev) => {
                if (checked) {
                    if (prev.length >= 4) {
                        if (!ceHas3rdCheckboxError) {
                            toast.error("Cannot select more than 4 on CE side.");
                            setCeHas3rdCheckboxError(true);
                        }
                        return prev;
                    } else {
                        const newArr = [...prev, rowKey];
                        if (newArr.length <= 4) setCeHas3rdCheckboxError(false);

                        LogManager.log('Selection', 'RowSelected', {
                            side: 'CE',
                            rowKey,
                            totalSelected: newArr.length
                        });

                        return newArr;
                    }
                } else {
                    const newArr = prev.filter((x) => x !== rowKey);
                    if (newArr.length < 4) setCeHas3rdCheckboxError(false);

                    LogManager.log('Selection', 'RowDeselected', {
                        side: 'CE',
                        rowKey,
                        totalSelected: newArr.length
                    });

                    return newArr;
                }
            });
        }
    };

    const formatTimeSince = (timestamp) => {
        if (!timestamp) return '';
        const now = new Date();
        const diffMs = now - timestamp;
        const hh = Math.floor(diffMs / (1000 * 60 * 60));
        const mm = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        const ss = Math.floor((diffMs % (1000 * 60)) / 1000);
        return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
    };

    // -------------------- Render --------------------
    return (
        <>
            {/* 2026-05-11 (#5): draggable active-broker logo, fixed-position above content */}
            <BrokerFlyingLogo />
            <div className="container">
                <ToastContainer position="top-left" />
                {isFreezed && (
                    <div
                        style={{
                            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                            background: 'rgba(0,0,0,0.5)', zIndex: 9999,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontSize: '1.5rem'
                        }}
                    >
                        <div>UI is frozen... (max 4s or until new LTP arrives)</div>
                    </div>
                )}

                <div className="header">
                    <div className="header-info" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {/* SEBI price-feed-stall alert (2026-08-06): shown only while a
                            resume notification is pending for the currently selected index.
                            Click anywhere on it to dismiss — visual only, monitoring continues
                            in the background regardless. */}
                        {stallAlert && (
                            <span
                                onClick={() => setStallAlert(null)}
                                title="Click to dismiss"
                                style={{
                                    background: '#e53935', color: '#fff', fontSize: '12px', fontWeight: 700,
                                    borderRadius: '4px', padding: '3px 8px', cursor: 'pointer',
                                    whiteSpace: 'nowrap', lineHeight: 1.4,
                                }}
                            >
                                {stallAlert.index} {stallAlert.price} ×
                            </span>
                        )}
                        <h2 style={{ fontSize: '18px' }}>
                            {instrument} - {expiryDate?.toISOString().split("T")[0]}
                        </h2>
                    </div>
                    <div className="header-controls">
                        {/* Broker Switch — Kite ⇄ Kotak backend toggle */}
                        <Dropdown className="mx-2" title="Switch trading backend">
                            <Dropdown.Toggle
                                variant={isDarkMode ? "dark" : "light"}
                                size="sm"
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                                    borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                                    padding: '4px 10px',
                                    border: `1px solid ${isDarkMode ? '#555' : '#ccc'}`,
                                }}
                                disabled={isFreezed}
                            >
                                <span
                                    style={{
                                        display: 'inline-block', width: '8px', height: '8px',
                                        borderRadius: '50%',
                                        background: activeBroker === "kotak" ? '#f97316' : '#2563eb',
                                    }}
                                />
                                {getBrokerDisplayName(activeBroker)}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => setActiveBroker("kite")}>
                                    <span style={{
                                        display: 'inline-block', width: '8px', height: '8px',
                                        borderRadius: '50%', background: '#2563eb',
                                        marginRight: '8px',
                                    }} />
                                    Kite {activeBroker === "kite" ? "✓" : ""}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => setActiveBroker("kotak")}>
                                    <span style={{
                                        display: 'inline-block', width: '8px', height: '8px',
                                        borderRadius: '50%', background: '#f97316',
                                        marginRight: '8px',
                                    }} />
                                    Kotak {activeBroker === "kotak" ? "✓" : ""}
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        {/* Market Protection % — accepts -1 (pure market) or 5-100 */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '8px' }}>
                            <span style={{ color: isDarkMode ? '#ccc' : '#333', fontSize: '13px', whiteSpace: 'nowrap' }}>MP%:</span>
                            <input
                                type="number"
                                min="-1"
                                max="100"
                                step="5"
                                value={marketProtection}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value, 10);
                                    if (!isNaN(val) && (val === -1 || (val >= 5 && val <= 100))) {
                                        setMarketProtection(val);
                                        localStorage.setItem("marketProtection", String(val));
                                    } else if (e.target.value === "" || e.target.value === "-") {
                                        setMarketProtection(e.target.value);
                                    }
                                }}
                                onBlur={() => {
                                    const val = parseInt(marketProtection, 10);
                                    if (isNaN(val) || (val !== -1 && val < 5)) {
                                        setMarketProtection(30);
                                        localStorage.setItem("marketProtection", "30");
                                    }
                                }}
                                style={{
                                    width: '55px', height: '30px', textAlign: 'center', fontSize: '13px',
                                    borderRadius: '4px', border: '1px solid #555',
                                    background: isDarkMode ? '#2a2a3d' : '#fff',
                                    color: marketProtection === -1 ? '#ff6b6b' : (isDarkMode ? '#fff' : '#000'),
                                }}
                                disabled={isFreezed}
                            />
                        </div>
                        {/* Iceberg Toggle */}
                        <Form.Check
                            type="switch"
                            id="iceberg-switch"
                            label="Iceberg"
                            checked={icebergEnabled}
                            onChange={(e) => setIcebergEnabled(e.target.checked)}
                            className="mx-2 check-margin-checkbox"
                            style={{ color: icebergEnabled ? 'orange' : (isDarkMode ? '#ffffff' : '#000000'), fontSize: '20px' }}
                            disabled={isFreezed}
                        />
                        {/* Orderbook Toggle — enables circuit limit retry with MP=-1 */}
                        <Form.Check
                            type="switch"
                            id="orderbook-switch"
                            label="Orderbook"
                            checked={orderbookEnabled}
                            onChange={(e) => {
                                const enabled = e.target.checked;
                                setOrderbookEnabled(enabled);
                                localStorage.setItem("orderbookEnabled", String(enabled));
                                axios.post(`${baseUrl}/set_orderbook_mode`, { enabled }).catch(err => {
                                    console.error("Failed to set orderbook mode:", err);
                                });
                                LogManager.log('Settings', 'OrderbookModeToggled', enabled);
                            }}
                            className="mx-2 check-margin-checkbox"
                            style={{ color: orderbookEnabled ? '#00ff88' : (isDarkMode ? '#ffffff' : '#000000'), fontSize: '20px' }}
                            disabled={isFreezed}
                        />
                        {/* Retry Limit (0-5) */}
                        <div className="d-flex align-items-center mx-2" style={{ gap: '4px' }}>
                            <span style={{ fontSize: '12px', color: isDarkMode ? '#aaa' : '#555', whiteSpace: 'nowrap' }}>Retry:</span>
                            <input
                                type="number"
                                min="0"
                                max="5"
                                value={retryLimit}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value, 10);
                                    if (!isNaN(val) && val >= 0 && val <= 5) {
                                        setRetryLimit(val);
                                        localStorage.setItem("retryLimit", String(val));
                                        axios.post(`${baseUrl}/set_retry_limit`, { limit: val }).catch(() => { });
                                    }
                                }}
                                style={{
                                    width: '38px', height: '24px', textAlign: 'center', fontSize: '12px',
                                    borderRadius: '4px', border: '1px solid #555',
                                    background: isDarkMode ? '#1e1e2f' : '#fff',
                                    color: retryLimit === 0 ? '#ff5252' : (isDarkMode ? '#fff' : '#000'),
                                }}
                                disabled={isFreezed}
                            />
                        </div>
                        <Form.Check
                            type="switch"
                            id="check-margin"
                            label="Check Margin"
                            checked={checkMargin}
                            onChange={(e) => {
                                const next = e.target.checked;
                                setCheckMargin(next);
                                LogManager.log('Settings', 'MarginCheckToggled', next);
                                // 2026-05-11: KOTAK-only auto-sync to /set_kite_margin.
                                //
                                // Why: the kotak backend has a separate `_kite_margin_enabled`
                                // flag that gates the S17 pre-flight (kite-basket margin
                                // pre-check before placing kotak orders). On May 7 that flag
                                // was set incorrectly mid-session and we had to flip it via
                                // a manual curl. This wires the checkbox to the server flag
                                // so they're always in sync — no manual API call needed.
                                //
                                // On kite broker we do NOT call /set_kite_margin — kite has
                                // no equivalent endpoint and we don't want a 404 every toggle.
                                if (getActiveBroker() === "kotak") {
                                    axios.post(`${baseUrl}/set_kite_margin`, { enabled: next })
                                        .then((r) => {
                                            LogManager.log('Settings', 'KotakKiteMarginSynced', {
                                                requested: next, server: r?.data,
                                            });
                                        })
                                        .catch((err) => {
                                            // Revert the checkbox so user sees the truth
                                            setCheckMargin(!next);
                                            const msg = err?.message || String(err);
                                            console.error("Failed to sync /set_kite_margin:", err);
                                            LogManager.log('Settings', 'KotakKiteMarginSyncFailed', {
                                                intended: next, error: msg,
                                            });
                                            toast.error(`Margin-check sync failed — reverted. (${msg})`);
                                        });
                                }
                            }}
                            className="mx-2 check-margin-checkbox"
                            style={{ color: isDarkMode ? '#ffffff' : '#000000', fontSize: '20px' }}
                            disabled={isFreezed}
                        />
                        {/* Rate-limit toggle (added 2026-04-27) — placed BEFORE ATM lock per spec.
                            ON = client-side 10 orders/1.2s throttle ACTIVE (safe default).
                            OFF = throttle bypassed; Kite may return 'Too many requests' 429.
                            State is server-authoritative; synced across UIs via Socket.IO. */}
                        <Form.Check
                            type="switch"
                            id="rate-limit-switch"
                            label={rateLimitEnabled ? "Rate Limit ON" : "Rate Limit OFF"}
                            checked={rateLimitEnabled}
                            onChange={(e) => {
                                const next = e.target.checked;
                                setRateLimitEnabled(next); // optimistic update for instant feedback
                                axios.post(`${baseUrl}/set_rate_limit`, { enabled: next })
                                    .catch((err) => {
                                        console.error("Failed to set rate_limit:", err);
                                        setRateLimitEnabled(!next); // revert on failure
                                    });
                                LogManager.log('Settings', 'RateLimitToggled', { enabled: next });
                            }}
                            className="mx-2 check-margin-checkbox"
                            style={{ color: rateLimitEnabled ? 'green' : 'red', fontSize: '20px' }}
                            disabled={isFreezed}
                        />
                        <Form.Check
                            type="switch"
                            id="strike-lock-switch"
                            label={isStrikeLockActive ? "Strike Locked" : "Strike Unlocked"}
                            checked={isStrikeLockActive}
                            onChange={(e) => {
                                freezeUI();
                                setIsStrikeLockActive(e.target.checked);
                                if (e.target.checked) {
                                    setLockedCeSideData(ceSideData);
                                    setLockedPeSideData(peSideData);
                                } else {
                                    setLockedCeSideData([]);
                                    setLockedPeSideData([]);
                                }

                                LogManager.log('Settings', 'StrikeLockToggled', {
                                    locked: e.target.checked
                                });
                            }}
                            className="mx-2 check-margin-checkbox"
                            style={{ color: isStrikeLockActive ? 'orange' : 'green', fontSize: '20px' }}
                            disabled={isFreezed}
                        />
                        <Button
                            variant="secondary"
                            onClick={openSettingsModal}
                            disabled={isFreezed}
                            className="mx-1"
                        >
                            Settings
                        </Button>
                        <Button
                            variant="info"
                            onClick={toggleLogViewer}
                            disabled={isFreezed}
                            className="mx-1"
                        >
                            Logs
                        </Button>
                        <Button
                            variant={isDarkMode ? "light" : "dark"}
                            onClick={() => setIsDarkMode(prev => !prev)}
                            className="mx-1"
                        >
                            {isDarkMode ? '☀ Light' : '🌙 Dark'}
                        </Button>
                    </div>
                </div>

                {data && (
                    <>
                        <div className="data-row">
                            <div>ATM VALUE: <span>{data?.ATM_VALUE}</span></div>
                            <div>Exact SynFut: <span>{data?.Exact_Synthetic_Future?.toFixed(2) || '0.00'}</span></div>
                        </div>

                        {/* PE SIDE */}
                        <div className="data-row pe-ce-row">
                            <div>
                                <span className="subsection-title">PE SIDE&nbsp;</span>
                                <span>Current Distance: </span>
                                <span>{Math.round(data?.PE_SIDE?.Required_Distance) || '0'}</span>
                            </div>
                            <div>
                                <span>Req. Distance:</span>
                                <span
                                    style={{ marginLeft: '8px', color: '#057fc3' }}
                                    className={peBlink ? 'blink' : ''}
                                >
                                    {Math.round(data?.PE_SIDE?.Default_Required_Distance) || '0'}
                                </span>
                                {peDistanceChange !== null && (
                                    <span style={{
                                        color: peDistanceChange >= 0 ? 'rgb(50,213,131)' : 'rgb(246,134,99)',
                                        marginLeft: '4px'
                                    }}>
                                        ({peDistanceChange > 0 ? '+' : ''}{Math.round(peDistanceChange)})
                                    </span>
                                )}
                                {peRequiredDistanceTimestamp && (
                                    <span style={{ marginLeft: '8px' }}>
                                        🕒{formatTimeSince(peRequiredDistanceTimestamp)}
                                    </span>
                                )}
                            </div>
                            <div>ITM PnL: <span>{data?.PE_SIDE?.ITM_Pnl?.toFixed(2) || '0.00'}</span></div>
                            <div>ITM PnL One Step: <span>{data?.PE_SIDE?.ITM_Pnl_one_step_closer?.toFixed(2) || '0.00'}</span></div>
                        </div>
                        <div className="data-row" style={{ marginBottom: '6px' }}>
                            {renderStrikeIntervalButtons('PE', data?.PE_SIDE?.Default_Required_Distance, data?.PE_SIDE?.Default_Required_Distance)}
                        </div>

                        {/* Toggle between T3 and T4 for PE */}
                        <div
                            className="exit-method"
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <Button
                                variant={!peS4 ? "primary" : "secondary"}
                                onClick={() => {
                                    freezeUI();
                                    setPeS4(false);
                                    fetchData();
                                    LogManager.log('Mode', 'Changed', {
                                        side: 'PE',
                                        mode: '3T'
                                    });
                                }}
                                disabled={isFreezed}
                                style={{ width: '30px', height: '30px', marginTop: '4px', backgroundColor: !peS4 ? 'yellow' : 'grey', color: 'black' }}
                            >
                                3T
                            </Button>
                            <Button
                                variant={peS4 ? "primary" : "secondary"}
                                onClick={() => {
                                    freezeUI();
                                    setPeS4(true);
                                    setPeS3Multiplier(1); // Reset multiplier when T4 is selected
                                    fetchData();
                                    LogManager.log('Mode', 'Changed', {
                                        side: 'PE',
                                        mode: '4T'
                                    });
                                }}
                                disabled={isFreezed}
                                style={{ width: '30px', height: '30px', marginTop: '4px', backgroundColor: peS4 ? 'yellow' : 'grey', color: 'black' }}
                            >
                                4T
                            </Button>
                            {renderS3MultiplierButtons('PE')}
                        </div>

                        <div className="exit-method">
                            <span className="label-text">EXIT METHOD</span>
                            <Form.Check
                                inline
                                label="S3 First"
                                name="peExitMethod"
                                type="radio"
                                id="pe-exit-s3"
                                checked={peExitMethod === 'S3'}
                                onChange={() => {
                                    setPeExitMethod('S3');
                                    LogManager.log('Settings', 'ExitMethodChanged', {
                                        side: 'PE',
                                        method: 'S3'
                                    });
                                }}
                                disabled={isFreezed}
                            />
                            <Form.Check
                                inline
                                label="S2 First"
                                name="peExitMethod"
                                type="radio"
                                id="pe-exit-s2"
                                checked={peExitMethod === 'S2'}
                                onChange={() => {
                                    setPeExitMethod('S2');
                                    LogManager.log('Settings', 'ExitMethodChanged', {
                                        side: 'PE',
                                        method: 'S2'
                                    });
                                }}
                                disabled={isFreezed}
                            />
                        </div>

                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>S1</th>
                                    <th>S1-LTP</th>
                                    <th>S2</th>
                                    <th>S2-LTP</th>
                                    <th style={
                                        (!peS4 && peS3Multiplier !== 1)
                                            ? { backgroundColor: 'yellow', color: 'black' }
                                            : {}
                                    }>
                                        {(!peS4 && peS3Multiplier !== 1) ? `S3 * ${peS3Multiplier}X` : 'S3'}
                                    </th>
                                    <th>S3-LTP</th>
                                    {peS4 && <th>S4</th>}
                                    {peS4 && <th>S4-LTP</th>}
                                    <th>PnL</th>
                                    <th>Select</th>
                                    <th>Place Order</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(isStrikeLockActive ? lockedPeSideData : peSideData)?.map((row) => {
                                    const rowKey = `PE-${row['S1-Strike']}`;
                                    const s1Ltp = ltpData[row['S1-Token']] || 0.0;
                                    const s2Ltp = ltpData[row['S2-Token']] || 0.0;
                                    const s3Ltp = ltpData[row['S3-Token']] || 0.0;

                                    const s4Strike = row['S4-Strike'];
                                    const s4Token = row['S4-Token'];
                                    const s4Ltp = s4Token ? (ltpData[s4Token] || 0.0) : 0.0;
                                    const hasS4 = (peS4 && s4Strike && s4Token);

                                    const realtimePnl = calculateCurrentPnL(row, 'PE', ltpData);

                                    const atmVal = data?.ATM_VALUE;
                                    const { label, color, rowClass } = getStrikeIndicator(row, 'PE', atmVal);

                                    const selectedAction = rowActions[rowKey] || null;
                                    const isChecked = peSelectedRows.includes(rowKey);

                                    return (
                                        <tr key={rowKey} className={rowClass}>
                                            <td><span style={{ color }}>{label}</span></td>
                                            <td>{row['S1-Strike']}</td>
                                            <td>{s1Ltp.toFixed(2)}</td>
                                            <td>{row['S2-Strike']}</td>
                                            <td>{s2Ltp.toFixed(2)}</td>
                                            <td>{row['S3-Strike']}</td>
                                            <td>{s3Ltp.toFixed(2)}</td>
                                            {peS4 && <td>{hasS4 ? s4Strike : ''}</td>}
                                            {peS4 && <td>{hasS4 ? s4Ltp.toFixed(2) : ''}</td>}
                                            <td style={{ color: realtimePnl >= 0 ? 'rgb(50,213,131)' : 'rgb(246,134,99)' }}>
                                                {realtimePnl.toFixed(2)}
                                            </td>
                                            <td>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={(e) => handleRowCheckboxChange('PE', rowKey, e.target.checked)}
                                                    disabled={isFreezed || (peSelectedRows.length >= 4 && !isChecked)}
                                                />
                                            </td>
                                            {renderPlaceOrderCell(row, 'PE', rowKey, selectedAction)}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {/* CE SIDE */}
                        <div className="data-row pe-ce-row">
                            <div>
                                <span className="subsection-title">CE SIDE&nbsp;</span>
                                <span>Current Distance: </span>
                                <span>{Math.round(data?.CE_SIDE?.Required_Distance) || '0'}</span>
                            </div>
                            <div>
                                <span>Req. Distance:</span>
                                <span
                                    style={{ marginLeft: '8px', color: '#057fc3' }}
                                    className={ceBlink ? 'blink' : ''}
                                >
                                    {Math.round(data?.CE_SIDE?.Default_Required_Distance) || '0'}
                                </span>
                                {ceDistanceChange !== null && (
                                    <span style={{
                                        color: ceDistanceChange >= 0 ? 'rgb(50,213,131)' : 'rgb(246,134,99)',
                                        marginLeft: '4px'
                                    }}>
                                        ({ceDistanceChange > 0 ? '+' : ''}{Math.round(ceDistanceChange)})
                                    </span>
                                )}
                                {ceRequiredDistanceTimestamp && (
                                    <span style={{ marginLeft: '8px' }}>
                                        🕒{formatTimeSince(ceRequiredDistanceTimestamp)}
                                    </span>
                                )}
                            </div>
                            <div>ITM PnL: <span>{data?.CE_SIDE?.ITM_Pnl?.toFixed(2) || '0.00'}</span></div>
                            <div>ITM PnL One Step: <span>{data?.CE_SIDE?.ITM_Pnl_one_step_closer?.toFixed(2) || '0.00'}</span></div>
                        </div>
                        <div className="data-row" style={{ marginBottom: '6px' }}>
                            {renderStrikeIntervalButtons('CE', data?.CE_SIDE?.Default_Required_Distance, data?.CE_SIDE?.Default_Required_Distance)}
                        </div>

                        {/* Toggle between T3 and T4 for CE */}
                        <div
                            className="exit-method"
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <Button
                                variant={!ceS4 ? "primary" : "secondary"}
                                onClick={() => {
                                    freezeUI();
                                    setCeS4(false);
                                    fetchData();
                                    LogManager.log('Mode', 'Changed', {
                                        side: 'CE',
                                        mode: '3T'
                                    });
                                }}
                                disabled={isFreezed}
                                style={{ width: '30px', height: '30px', marginTop: '4px', backgroundColor: !ceS4 ? 'yellow' : 'grey', color: 'black' }}
                            >
                                3T
                            </Button>
                            <Button
                                variant={ceS4 ? "primary" : "secondary"}
                                onClick={() => {
                                    freezeUI();
                                    setCeS4(true);
                                    setCeS3Multiplier(1); // Reset multiplier when T4 is selected
                                    fetchData();
                                    LogManager.log('Mode', 'Changed', {
                                        side: 'CE',
                                        mode: '4T'
                                    });
                                }}
                                disabled={isFreezed}
                                style={{ width: '30px', height: '30px', marginTop: '4px', backgroundColor: ceS4 ? 'yellow' : 'grey', color: 'black' }}
                            >
                                4T
                            </Button>
                            {renderS3MultiplierButtons('CE')}
                        </div>

                        <div className="exit-method">
                            <span className="label-text">EXIT METHOD</span>
                            <Form.Check
                                inline
                                label="S3 First"
                                name="ceExitMethod"
                                type="radio"
                                id="ce-exit-s3"
                                checked={ceExitMethod === 'S3'}
                                onChange={() => {
                                    setCeExitMethod('S3');
                                    LogManager.log('Settings', 'ExitMethodChanged', {
                                        side: 'CE',
                                        method: 'S3'
                                    });
                                }}
                                disabled={isFreezed}
                            />
                            <Form.Check
                                inline
                                label="S2 First"
                                name="ceExitMethod"
                                type="radio"
                                id="ce-exit-s2"
                                checked={ceExitMethod === 'S2'}
                                onChange={() => {
                                    setCeExitMethod('S2');
                                    LogManager.log('Settings', 'ExitMethodChanged', {
                                        side: 'CE',
                                        method: 'S2'
                                    });
                                }}
                                disabled={isFreezed}
                            />
                        </div>

                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>S1</th>
                                    <th>S1-LTP</th>
                                    <th>S2</th>
                                    <th>S2-LTP</th>
                                    <th style={
                                        (!ceS4 && ceS3Multiplier !== 1)
                                            ? { backgroundColor: 'yellow', color: 'black' }
                                            : {}
                                    }>
                                        {(!ceS4 && ceS3Multiplier !== 1) ? `S3 * ${ceS3Multiplier}X` : 'S3'}
                                    </th>
                                    <th>S3-LTP</th>
                                    {ceS4 && <th>S4</th>}
                                    {ceS4 && <th>S4-LTP</th>}
                                    <th>PnL</th>
                                    <th>Select</th>
                                    <th>Place Order</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(isStrikeLockActive ? lockedCeSideData : ceSideData)?.map((row) => {
                                    const rowKey = `CE-${row['S1-Strike']}`;
                                    const s1Ltp = ltpData[row['S1-Token']] || 0.0;
                                    const s2Ltp = ltpData[row['S2-Token']] || 0.0;
                                    const s3Ltp = ltpData[row['S3-Token']] || 0.0;

                                    const s4Strike = row['S4-Strike'];
                                    const s4Token = row['S4-Token'];
                                    const s4Ltp = s4Token ? (ltpData[s4Token] || 0.0) : 0.0;
                                    const hasS4 = (ceS4 && s4Strike && s4Token);

                                    const realtimePnl = calculateCurrentPnL(row, 'CE', ltpData);

                                    const atmVal = data?.ATM_VALUE;
                                    const { label, color, rowClass } = getStrikeIndicator(row, 'CE', atmVal);

                                    const selectedAction = rowActions[rowKey] || null;
                                    const isChecked = ceSelectedRows.includes(rowKey);

                                    return (
                                        <tr key={rowKey} className={rowClass}>
                                            <td><span style={{ color }}>{label}</span></td>
                                            <td>{row['S1-Strike']}</td>
                                            <td>{s1Ltp.toFixed(2)}</td>
                                            <td>{row['S2-Strike']}</td>
                                            <td>{s2Ltp.toFixed(2)}</td>
                                            <td>{row['S3-Strike']}</td>
                                            <td>{s3Ltp.toFixed(2)}</td>
                                            {ceS4 && <td>{hasS4 ? s4Strike : ''}</td>}
                                            {ceS4 && <td>{hasS4 ? s4Ltp.toFixed(2) : ''}</td>}
                                            <td style={{ color: realtimePnl >= 0 ? 'rgb(50,213,131)' : 'rgb(246,134,99)' }}>
                                                {realtimePnl.toFixed(2)}
                                            </td>
                                            <td>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={(e) => handleRowCheckboxChange('CE', rowKey, e.target.checked)}
                                                    disabled={isFreezed || (ceSelectedRows.length >= 4 && !isChecked)}
                                                />
                                            </td>
                                            {renderPlaceOrderCell(row, 'CE', rowKey, selectedAction)}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </>
                )}
            </div>

            {/* Settings Modal */}
            <Modal show={showSettings} onHide={() => setShowSettings(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Settings</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Instrument</Form.Label>
                        <div className="modal-button-group">
                            {Object.keys(lotSizeOptions).map((inst, idx) => {
                                return (
                                    <Button
                                        key={idx}
                                        variant={modalInstrument === inst ? "success" : "secondary"}
                                        onClick={() => {
                                            setModalInstrument(inst);
                                            setModalLotSizes(lotSizeOptions[inst].slice(0, 3));
                                            setModalExpiryDate(expiryDates[inst]?.[0] || null);
                                        }}
                                        className="m-1"
                                    >
                                        {inst}
                                    </Button>
                                );
                            })}
                        </div>
                    </Form.Group>

                    <Form.Group className="mt-3">
                        <Form.Label>Expiry Date</Form.Label>
                        <div className="modal-button-group">
                            {expiryDates[modalInstrument]?.slice().sort((a, b) => a - b).map((date, idx2) => {
                                return (
                                    <Button
                                        key={idx2}
                                        variant={
                                            modalExpiryDate?.toISOString().split("T")[0] === date.toISOString().split("T")[0]
                                                ? "success"
                                                : "secondary"
                                        }
                                        onClick={() => setModalExpiryDate(date)}
                                        className="m-1"
                                    >
                                        {date.toISOString().split("T")[0]}
                                    </Button>
                                );
                            })}
                        </div>
                    </Form.Group>

                    <Form.Group className="mt-3">
                        <Form.Label>Lot Sizes (Select up to 3)</Form.Label>
                        <div className="modal-button-group">
                            {lotSizeOptions[modalInstrument]?.map((option, idx3) => (
                                <Button
                                    key={idx3}
                                    variant={modalLotSizes.includes(option) ? "success" : "secondary"}
                                    onClick={() => {
                                        setModalLotSizes((prev) => {
                                            const updated = [...prev];
                                            if (updated.includes(option)) {
                                                return updated.filter((sz) => sz !== option);
                                            } else if (updated.length < 3) {
                                                updated.push(option);
                                            }
                                            return updated;
                                        });
                                    }}
                                    className="m-1"
                                >
                                    {option}
                                </Button>
                            ))}
                        </div>
                        <div className="mt-3">
                            <Form.Label>Custom Lot Sizes</Form.Label>
                            {[0, 1, 2].map((idx4) => (
                                <Form.Control
                                    key={idx4}
                                    type="number"
                                    placeholder="Enter custom lot size"
                                    value={modalCustomLotSizes[idx4]}
                                    onChange={(e) =>
                                        handleCustomLotSizeChange(idx4, parseInt(e.target.value) || 0)
                                    }
                                    className="m-1"
                                />
                            ))}
                        </div>
                    </Form.Group>

                    <Form.Group className="mt-3">
                        <Form.Label>Total Rows</Form.Label>
                        <div className="modal-button-group">
                            {[10, 12, 14, 16, 20, 25, 30, 35, 40].map((n) => (
                                <Button
                                    key={n}
                                    variant={modalNumRows === n ? "success" : "secondary"}
                                    onClick={() => setModalNumRows(n)}
                                    className="m-1"
                                >
                                    {n}
                                </Button>
                            ))}
                        </div>
                        <Form.Label>Total ITMs</Form.Label>
                        <div className="modal-button-group">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((n2) => (
                                <Button
                                    key={n2}
                                    variant={modalNumItms === n2 ? "success" : "secondary"}
                                    onClick={() => setModalNumItms(n2)}
                                    className="m-1"
                                >
                                    {n2}
                                </Button>
                            ))}
                        </div>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSettings(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleUpdateSettings}>
                        Update
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Log Viewer Modal */}
            <Modal
                show={showLogs}
                onHide={() => setShowLogs(false)}
                centered
                size="lg"
                dialogClassName="log-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Application Logs</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <Form.Control
                            type="text"
                            placeholder="Filter logs..."
                            value={logFilter}
                            onChange={(e) => setLogFilter(e.target.value)}
                        />
                    </div>
                    <div
                        style={{
                            maxHeight: '500px',
                            overflowY: 'scroll',
                            backgroundColor: '#f5f5f5',
                            padding: '10px',
                            borderRadius: '5px'
                        }}
                    >
                        {logEntries.filter(filterLogs).map((entry, index) => (
                            <div
                                key={index}
                                style={{
                                    marginBottom: '8px',
                                    padding: '5px',
                                    borderBottom: '1px solid #ddd',
                                    fontSize: '12px',
                                    fontFamily: 'monospace',
                                    color: 'black'
                                }}
                            >
                                <div style={{ fontWeight: 'bold' }}>
                                    {entry.timestamp} | {entry.component} | {entry.action}
                                </div>
                                <pre style={{ marginLeft: '20px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                    {entry.details}
                                </pre>
                            </div>
                        ))}
                        {logEntries.filter(filterLogs).length === 0 && (
                            <div style={{ textAlign: 'center', padding: '20px', color: 'black' }}>
                                No logs found matching filter criteria
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={fetchRecentLogs}>
                        Refresh Logs
                    </Button>
                    <Button variant="secondary" onClick={() => setShowLogs(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <ReactHowler src="3T-Alert.mp3" playing={shouldPlaySound} />

            {/* ── Floating Amber Dot — Failed Order Notifications ── */}
            {failedOrderNotifications.length > 0 && (
                <div
                    style={{
                        position: 'fixed',
                        left: dotPosition.x,
                        top: dotPosition.y,
                        width: '26px',
                        height: '26px',
                        borderRadius: '50%',
                        background: '#ff9800',
                        boxShadow: '0 0 10px rgba(255,152,0,0.8), 0 0 20px rgba(255,152,0,0.4)',
                        cursor: 'pointer',
                        zIndex: 99999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        animation: 'failDotPulse 1.5s infinite',
                        userSelect: 'none',
                    }}
                    onClick={() => setShowFailPopup(true)}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        const startX = e.clientX - dotPosition.x;
                        const startY = e.clientY - dotPosition.y;
                        let moved = false;
                        const onMove = (ev) => {
                            moved = true;
                            const newX = Math.max(0, Math.min(window.innerWidth - 30, ev.clientX - startX));
                            const newY = Math.max(0, Math.min(window.innerHeight - 30, ev.clientY - startY));
                            setDotPosition({ x: newX, y: newY });
                        };
                        const onUp = () => {
                            document.removeEventListener('mousemove', onMove);
                            document.removeEventListener('mouseup', onUp);
                            if (moved) {
                                const pos = { x: dotPosition.x, y: dotPosition.y };
                                localStorage.setItem("failDotPosition", JSON.stringify(pos));
                            }
                        };
                        document.addEventListener('mousemove', onMove);
                        document.addEventListener('mouseup', onUp);
                    }}
                    title={`${failedOrderNotifications.length} failed order(s) — click to view`}
                >
                    {failedOrderNotifications.length}
                </div>
            )}

            {/* ── Enhanced Failed Orders Popup ── */}
            {showFailPopup && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.6)', zIndex: 100000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{
                        background: isDarkMode ? '#1e1e2f' : '#fff',
                        borderRadius: '12px', padding: '20px', minWidth: '450px', maxWidth: '650px',
                        maxHeight: '75vh', overflowY: 'auto',
                        border: '2px solid #ff9800', boxShadow: '0 0 30px rgba(255,152,0,0.3)',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h5 style={{ margin: 0, color: '#ff9800' }}>Failed Orders ({failedOrderNotifications.length})</h5>
                            <button
                                onClick={() => setShowFailPopup(false)}
                                style={{
                                    background: 'none', border: 'none', color: isDarkMode ? '#fff' : '#000',
                                    fontSize: '22px', cursor: 'pointer', padding: '0 5px',
                                }}
                            >&times;</button>
                        </div>
                        {failedOrderNotifications.length === 0 ? (
                            <p style={{ color: isDarkMode ? '#aaa' : '#666', textAlign: 'center' }}>No failed orders</p>
                        ) : (
                            failedOrderNotifications.map((notif) => {
                                // Parse tradingsymbol for readable display
                                // e.g. NIFTY2640722600CE → NIFTY 22600 CE
                                // Uses known index names to split correctly
                                const sym = notif.tradingsymbol || '';
                                // Parse: NIFTY2640722600CE → NIFTY 22600 CE
                                // Known indices sorted longest-first to avoid NIFTY matching BANKNIFTY
                                // 2026-05-14: CRUDEOIL added (MCX commodity options).
                                const knownIndices = ['MIDCPNIFTY', 'BANKNIFTY', 'FINNIFTY', 'NIFTY', 'SENSEX', 'BANKEX', 'CRUDEOIL'];
                                const strikeIntervals = { NIFTY: 50, BANKNIFTY: 100, FINNIFTY: 50, MIDCPNIFTY: 25, SENSEX: 100, BANKEX: 100, CRUDEOIL: 50 };
                                let readableStrike = sym;
                                for (const idx of knownIndices) {
                                    if (sym.startsWith(idx)) {
                                        // Extract CE/PE suffix and everything between index and option type
                                        const afterIdx = sym.slice(idx.length); // "2640722600CE"
                                        const optMatch = afterIdx.match(/(CE|PE)$/);
                                        if (optMatch) {
                                            const optType = optMatch[1]; // "CE" or "PE"
                                            const digits = afterIdx.slice(0, -2); // "2640722600"
                                            // Strike price is at the end of digits. Try extracting last 3,4,5,6 digits
                                            // and check if it's divisible by the strike interval
                                            const interval = strikeIntervals[idx] || 50;
                                            let strike = '';
                                            // Max realistic strike prices per index
                                            const maxStrikes = { NIFTY: 99999, BANKNIFTY: 99999, FINNIFTY: 99999, MIDCPNIFTY: 99999, SENSEX: 200000, BANKEX: 99999, CRUDEOIL: 99999 };
                                            const maxStrike = maxStrikes[idx] || 99999;
                                            for (let len = 3; len <= Math.min(6, digits.length - 1); len++) {
                                                const candidate = digits.slice(-len);
                                                const num = parseInt(candidate, 10);
                                                if (num > 0 && num % interval === 0 && num <= maxStrike) {
                                                    strike = candidate;
                                                    // Don't break — keep trying longer to get the full strike
                                                }
                                            }
                                            if (!strike && digits.length > 5) {
                                                // Fallback: assume 5-char expiry, rest is strike
                                                strike = digits.slice(5);
                                            }
                                            readableStrike = strike ? `${idx} ${parseInt(strike, 10)} ${optType}` : sym;
                                        }
                                        break;
                                    }
                                }

                                return (
                                    <div key={notif.id} style={{
                                        background: isDarkMode ? '#2a2a3d' : '#f9f0f0',
                                        borderRadius: '8px', padding: '12px', marginBottom: '10px',
                                        border: '1px solid rgba(255,152,0,0.3)',
                                    }}>
                                        {/* Row 1: Strike info + dismiss */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>
                                                <strong style={{ color: notif.direction === 'BUY' ? '#4caf50' : '#ff5252' }}>
                                                    {notif.direction}
                                                </strong>
                                                {' '}<strong>{readableStrike}</strong>
                                                <span style={{ color: '#ff9800', marginLeft: '8px' }}>
                                                    Qty: {notif.quantity}
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => setFailedOrderNotifications(prev => prev.filter(n => n.id !== notif.id))}
                                                style={{
                                                    background: 'none', border: 'none',
                                                    color: '#ff9800', fontSize: '18px', cursor: 'pointer',
                                                }}
                                            >&times;</button>
                                        </div>
                                        {/* Row 2: Error message */}
                                        <div style={{ fontSize: '11px', color: '#ff8888', marginTop: '4px' }}>
                                            {notif.error}
                                        </div>
                                        {/* Row 3: MP% input + Place Order button */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                                            <label style={{ fontSize: '12px', color: isDarkMode ? '#aaa' : '#555', whiteSpace: 'nowrap' }}>MP%:</label>
                                            <input
                                                type="number"
                                                min="-1"
                                                max="100"
                                                defaultValue={notif._retryMp || parseInt(localStorage.getItem("marketProtection") || "30", 10)}
                                                onChange={(e) => {
                                                    const val = parseInt(e.target.value, 10);
                                                    notif._retryMp = val;
                                                }}
                                                style={{
                                                    width: '50px', height: '26px', textAlign: 'center', fontSize: '12px',
                                                    borderRadius: '4px', border: '1px solid #555',
                                                    background: isDarkMode ? '#1a1a2e' : '#fff',
                                                    color: isDarkMode ? '#fff' : '#000',
                                                }}
                                            />
                                            <button
                                                onClick={async () => {
                                                    const mp = notif._retryMp ?? parseInt(localStorage.getItem("marketProtection") || "30", 10);
                                                    // Determine exchange and index from tradingsymbol
                                                    const indices = ['MIDCPNIFTY', 'BANKNIFTY', 'FINNIFTY', 'NIFTY', 'SENSEX', 'BANKEX', 'CRUDEOIL'];   // 2026-05-14: MCX
                                                    let matchedIndex = 'NIFTY';
                                                    for (const idx of indices) {
                                                        if (notif.tradingsymbol.startsWith(idx)) { matchedIndex = idx; break; }
                                                    }
                                                    const exchange = matchedIndex === 'CRUDEOIL' ? 'MCX' : (matchedIndex === 'SENSEX' || matchedIndex === 'BANKEX') ? 'BFO' : 'NFO';   // 2026-05-14: MCX route
                                                    const payload = {
                                                        instrument: notif.tradingsymbol,
                                                        quantity: notif.quantity,
                                                        transaction_type: notif.direction,
                                                        order_type: "regular",
                                                        type: exchange,
                                                        seletedIndex: matchedIndex,
                                                        market_protection: mp,
                                                    };
                                                    try {
                                                        const encoded = encodeURIComponent(JSON.stringify(payload));
                                                        const resp = await axios.get(`${baseUrl}/placeorder?payload=${encoded}`, {
                                                            headers: { "ngrok-skip-browser-warning": "69420" }
                                                        });
                                                        const body = typeof resp.data === 'string' ? resp.data : JSON.stringify(resp.data);
                                                        if (body.includes('Success') || body.includes('"ID"')) {
                                                            setFailedOrderNotifications(prev => prev.filter(n => n.id !== notif.id));
                                                            playOrderSuccessSound();  // 2026-05-11 (#3): kotak-only ding on retry success
                                                        } else {
                                                            notif.error = `Retry failed: ${body.substring(0, 150)}`;
                                                            setFailedOrderNotifications(prev => [...prev]);
                                                        }
                                                    } catch (err) {
                                                        const errData = err.response?.data;
                                                        const errMsg = typeof errData === 'object' ? JSON.stringify(errData) : (errData || err.message);
                                                        notif.error = `Retry error: ${errMsg.substring(0, 150)}`;
                                                        setFailedOrderNotifications(prev => [...prev]);
                                                    }
                                                }}
                                                style={{
                                                    padding: '4px 12px', fontSize: '12px', fontWeight: 'bold',
                                                    background: '#ff9800', color: '#fff', border: 'none',
                                                    borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap',
                                                }}
                                            >Place Order</button>
                                        </div>
                                        {/* Row 4: Timestamp */}
                                        <div style={{ fontSize: '10px', color: isDarkMode ? '#555' : '#999', marginTop: '4px' }}>
                                            {notif.timestamp} | {notif.execution_id}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                        {failedOrderNotifications.length > 0 && (
                            <button
                                onClick={() => {
                                    setFailedOrderNotifications([]);
                                    setShowFailPopup(false);
                                }}
                                style={{
                                    width: '100%', marginTop: '10px', padding: '8px',
                                    background: '#ff9800', color: '#fff', border: 'none',
                                    borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
                                }}
                            >Clear All</button>
                        )}
                    </div>
                </div>
            )}

            {/* Pulse animation for amber dot */}
            <style>{`
                @keyframes failDotPulse {
                    0% { box-shadow: 0 0 5px rgba(255,152,0,0.8); }
                    50% { box-shadow: 0 0 15px rgba(255,152,0,1), 0 0 30px rgba(255,152,0,0.5); }
                    100% { box-shadow: 0 0 5px rgba(255,152,0,0.8); }
                }
            `}</style>
        </>
    );
};

export default App; 